import { useCallback, useEffect, useRef, useState, type Dispatch, type SetStateAction } from "react";
import { sendChat, stopChat } from "../api/client";
import { reattachChat } from "../api/reattach";
import { createConversation, listConversations } from "../api/conversations";
import { fetchTurns, getCachedTurns, invalidateTurns } from "../api/turnsCache";
import { HttpError } from "../api/errors";
import { friendlyError, type FriendlyError } from "../api/errorMessages";
import { isSoundOnDone, playChime } from "../lib/chime";
import {
  QUEUE_WAIT_MS,
  QUEUE_WAITERS_BACKOFF_MS,
  awaitQueueStart,
  clearQueued,
  deleteQueued,
  editQueued,
  enqueuePrompt,
  getQueue,
  moveQueuedItem,
  resumeQueue as resumeQueueApi,
} from "../api/queue";
import {
  mergeStoredIds,
  rehydrateStoredAttachments,
  storedAttachment,
  toOutgoing,
  type Attachment,
  type OutgoingImage,
} from "../lib/attachments";
import type {
  AttachmentMeta,
  EnqueueResult,
  NextStart,
  ProgressEvent,
  ProgressKind,
  QueueItemWire,
  QueueState,
  SnapshotEvent,
  StreamEvent,
  TurnRow,
  TurnStatus,
} from "../api/types";

export type ProgressEntry = {
  id: string;
  kind: ProgressKind;
  label: string;
  ts: number;
  /** Deep mode: the sub-agent this entry came from (Agent/Task tool_use id), if
   * it originated inside a panel agent. Absent for main-agent progress. */
  agentId?: string;
  /** The sub-agent's type (e.g. "general-purpose"), when known. */
  agentType?: string;
  /** True for the entry that spawned a sub-agent (a panel launch). */
  agentSpawn?: boolean;
};

/**
 * A prompt waiting in the server-owned queue. `id` is stable for React and the
 * Composer: the client id for a prompt queued here (it is also the server's
 * `client_id`), `s-<serverId>` for one loaded from the server without one.
 * `status` is absent once the server has the prompt, "sending" while its POST
 * is in flight, and "failed" when the server refused it (the Composer offers
 * Retry).
 */
export type QueuedPrompt = {
  id: string;
  text: string;
  attachments: Attachment[];
  serverId?: number;
  status?: "sending" | "failed";
};

/**
 * An image on a turn in the transcript, from either of the two places one can
 * come from. A turn sent in this session still holds the `Attachment` the
 * browser encoded, so it renders straight from its data URL with no round-trip;
 * a turn loaded from history only has the daemon's manifest, so it renders from
 * the serving route, which needs the conversation id as well as the image id.
 * One union rather than two fields keeps the order the user attached them in,
 * and keeps Message.tsx to a single loop.
 */
export type MessageAttachment =
  | { kind: "local"; attachment: Attachment }
  | { kind: "stored"; conversationId: number; meta: AttachmentMeta };

export type ChatMessage = {
  id: string;
  role: "user" | "assistant";
  content: string;
  streaming: boolean;
  error?: string;
  errorInfo?: FriendlyError;
  progress?: ProgressEntry[];
  /**
   * Deep mode / long turns: how many progress entries were dropped off the FRONT
   * to keep `progress` bounded at MAX_PROGRESS_ENTRIES. A big read-only panel can
   * emit hundreds–thousands of frames; storing and rendering all of them makes
   * the append O(n^2) and the DOM huge. We keep the most recent K and surface the
   * count of older ones as a "+N earlier steps" indicator. 0/undefined for a
   * normal turn that never hit the cap (rendered identically to before).
   */
  progressOverflow?: number;
  interrupted?: boolean;
  /**
   * When this turn started (ms epoch), set the moment the prompt is sent. Drives
   * the live "thinking" timer. Only present on assistant turns produced in this
   * session; history turns loaded from the server omit it (and show no timer).
   */
  startedAt?: number;
  /**
   * Marks a USER turn that was injected to resume a stalled response (an
   * automatic stall-resume or a one-click Continue), so the UI can render it as
   * a compact "continue" chip instead of a full bubble. Purely presentational —
   * the server still records a normal "continue" turn, so a reload shows it as a
   * plain user message with no desync.
   */
  autoContinue?: boolean;
  /**
   * Marks an ASSISTANT turn that ended abnormally and is about to be resumed
   * automatically, so the UI can show a transient, non-alarming "resuming…"
   * indicator. Cleared when the next turn starts.
   */
  resuming?: boolean;
  /**
   * Overrides the wording of the `resuming` indicator. A deep-mode budget stop
   * is continued deliberately in smaller steps, not retried after a fault, so
   * the default "Resuming automatically…" would name the wrong event. Absent on
   * an ordinary stall-resume, which keeps the default.
   */
  resumingLabel?: string;
  /**
   * Marks an ASSISTANT turn whose automatic resumes are exhausted (or that we
   * chose not to auto-resume) and which offers a one-click Continue button.
   */
  needsContinue?: boolean;
  /**
   * Images on this turn, rendered above its text. Absent — never an empty array
   * — on a turn that carries none, so a text-only message is exactly the object
   * it was before attachments existed.
   */
  attachments?: MessageAttachment[];
};

export type UseChatStream = {
  messages: ChatMessage[];
  send: (text: string, attachments?: Attachment[]) => Promise<void>;
  stop: () => Promise<void>;
  isStreaming: boolean;
  isLoadingHistory: boolean;
  authError: boolean;
  clearAuthError: () => void;
  /**
   * Last non-auth error from history-load or chat-send. Preserves the original
   * Error instance (including HttpError) so consumers can branch on
   * `error instanceof HttpError && error.status === 403` for the forbidden UI.
   */
  error: Error | null;
  /** Friendly version of `error` for the history-load banner. */
  errorInfo: FriendlyError | null;
  clearError: () => void;
  loadLatestHistory: () => Promise<void>;
  loadConversation: (conversationId: number) => Promise<void>;
  startNew: () => Promise<void>;
  currentConversationId: number | null;
  /** One-click resume of a stalled turn: sends `continue` to this conversation. */
  continueTurn: () => void;
  /** True when a finalized turn offers a manual Continue and nothing is streaming. */
  canContinue: boolean;
  /** True while an automatic stall-resume is scheduled (the backoff window). */
  isResuming: boolean;
  /** Prompts submitted while a turn was streaming, awaiting their turn (FIFO). */
  queue: QueuedPrompt[];
  /** Drop a queued prompt by its stable id. */
  removeQueued: (id: string) => void;
  /** Replace a queued prompt's text and images in place, keeping its position. */
  updateQueued: (id: string, text: string, attachments?: Attachment[]) => void;
  /**
   * Move a queued prompt to `toIndex` (clamped to the queue); every other
   * prompt keeps its relative order. Index 0 is the next prompt to run.
   * Returns whether anything moved, so a caller can react (focus, announce)
   * only to a real reorder.
   */
  moveQueued: (id: string, toIndex: number) => boolean;
  /** Clear the entire queue. */
  clearQueue: () => void;
  /**
   * True when the server paused this chat's queue (Stop, a failed turn, a
   * restart). False while an automatic continuation that will resume the
   * queue is pending or running, so no Resume button races it.
   */
  queuePaused: boolean;
  /** Why, as a daemon reason; lib/queuePause.ts turns it into words. */
  queuePausedReason: string | null;
  /** Clear the pause and let the server start the next prompt. */
  resumeQueue: () => void;
  /** Send a prompt the server refused to save once more, under the same client id. */
  retryQueued: (id: string) => void;
};

const AUTH_ERROR_MARKERS = new Set(["unauthorized", "no token configured"]);

// Upper bound on stored/rendered progress entries per assistant message. A wide
// read-only deep-mode panel emits hundreds–thousands of progress frames; the
// per-frame append copies the array (O(n) → O(n^2) over a turn) and Message.tsx
// renders every entry. We keep the most recent K and count the rest as overflow
// so a huge fan-out stays responsive; a normal turn never reaches the cap and is
// visually unchanged.
const MAX_PROGRESS_ENTRIES = 200;

// Progress kinds that eviction must never drop. A tool step is a transcript of
// something the turn already did, so losing the oldest ones costs the user
// nothing but scrollback. `sibling_conflict` is not a step: it reports that
// another conversation on this branch changed a file this tree also has
// uncommitted work on, and the daemon reports a given divergence EXACTLY ONCE —
// the sync deliberately leaves a both-modified path alone rather than stranding
// a marker forever, so the next sync says nothing. That single frame is the
// turn's FIRST progress entry and therefore the first thing front-eviction
// takes, in exactly the wide turns with the most cross-conversation churn.
// Evicted, it is not delayed; it is the only warning the user will ever get,
// silently discarded, and their next commit supersedes the sibling's version
// with no trace. Raising the cap only moves the cliff, so exempt it instead.
const PROGRESS_EVICTION_EXEMPT = new Set<ProgressEntry["kind"]>(["sibling_conflict"]);

// A stalled turn is auto-resumed at most this many times before falling back to
// the manual Continue button.
const MAX_AUTO_CONTINUE = 2;

// ---- Guard-stop continuation (separate from the stall chain above) ---------
// A turn stopped by one of the daemon's deliberate GUARDS is NOT a stall and
// must never join the generic retry chain: that chain resends "continue" into
// the identical fan-out and trips the identical guard, which is a loop that
// spends on every lap. So every guard code stays in NO_AUTO_CONTINUE_CODES, and
// this single-shot continuation sits beside it.
//
// It is worth doing at all because the stop is RECOVERABLE in a way a stall is
// not: the server-side CLI session survives the kill via `--resume` and the
// worktree keeps the edits, so the next turn genuinely continues rather than
// restarting. What the member reported was "larger tasks should automatically be
// split into multiple steps", and this is that split, performed once.
//
// The message does the steering the generic "continue" cannot: it tells the
// model not to launch the panel again (launching it again is what hit the
// guard) and to end with a remaining-work list, so a third turn has somewhere
// to start. Each guard gets its OWN wording, because what the model must do
// differently is not the same for a budget stop and a write block.
//
// METERING-FREE, and that is a hard requirement rather than a style choice.
// This is persisted as a REAL user turn, so after a reload, and for anyone else
// viewing the conversation, it renders as a message in the member's own name.
// An earlier draft opened with "That step reached its budget and was stopped",
// which would have told the member their spend is metered in the one surface
// errorMessages.ts exists to keep that out of. It says what to DO and never why
// the turn ended.
const DEEP_STOP_CONTINUE_MESSAGE =
  "Continue from where you stopped, in smaller steps: finish or commit what is in progress, do not launch the parallel panel again, and end with a short list of what remains.";
// The write-block sibling. Same metering-free rule, different instruction: the
// budget stop wants smaller steps, this one wants the work done in the MAIN
// flow, because a sub-agent is read-only and re-delegating hits the same wall
// however small the step is. Named separately rather than shared so neither
// message can be reworded into the other by accident.
const WRITE_BLOCK_CONTINUE_MESSAGE =
  "Continue from where you stopped in your own main flow: do not launch parallel sub-agents again, make the remaining changes yourself one step at a time, and end with a short list of what remains.";
// Short pause before the continuation, mirroring the first stall backoff. It is
// not a retry backoff (nothing here is transient); it is the window in which the
// "continuing" affordance is visible, so the turn does not appear to restart on
// its own with no explanation.
const GUARD_STOP_CONTINUE_DELAY_MS = 800;
// Codes where offering to resend the steering message would be offering to
// spend into, or fail against, the identical wall. A guard continuation that
// dies on one of these gets the plain card, not a button: the money ones are
// the same argument NO_AUTO_CONTINUE_CODES makes, and an over-ceiling context
// is over the ceiling however the turn is phrased.
const GUARD_CONTINUE_WALL_CODES: ReadonlySet<string> = new Set([
  "credits_exhausted",
  "daily_cap",
  "monthly_cap",
  "context_exhausted",
]);

// Shown instead of "Resuming automatically…" while the continuation is pending.
// This turn was not interrupted by a fault, so the generic wording would
// describe the wrong event to the one person who can see it.
const DEEP_STOP_RESUMING_LABEL = "Continuing in smaller steps…";
const WRITE_BLOCK_RESUMING_LABEL = "Continuing without the panel…";

/** What one guard stop sends, and what the member sees while it is pending. */
type GuardStopContinuation = { message: string; resumingLabel: string };

/**
 * The guard stops that earn ONE automatic continuation, keyed by daemon error
 * code. Adding a guard is adding an entry here: every site below reads the
 * table rather than naming a code, so a new guard needs no new branch.
 *
 * Membership here does NOT loosen NO_AUTO_CONTINUE_CODES: every key is also in
 * that set, deliberately. The two mechanisms are separate: this is one steered
 * continuation with its own latch, that is the generic unsteered retry chain,
 * and a code that left the set would get BOTH.
 *
 * The latch is shared across the whole table, so a prompt that trips a budget
 * stop and then a write block gets one continuation in total, not one each.
 */
export const GUARD_STOP_CONTINUATIONS: Readonly<Record<string, GuardStopContinuation>> = {
  deep_mode_limit: {
    message: DEEP_STOP_CONTINUE_MESSAGE,
    resumingLabel: DEEP_STOP_RESUMING_LABEL,
  },
  // A panel sub-agent tried to write the shared worktree and the daemon killed
  // the turn. Resumable for the same reason the budget stop is: the CLI session
  // survives via `--resume` and the worktree keeps whatever landed, so the next
  // turn continues the work instead of restarting it. Without this the card was
  // a dead end and retyping the prompt was the only way on.
  subagent_write_blocked: {
    message: WRITE_BLOCK_CONTINUE_MESSAGE,
    resumingLabel: WRITE_BLOCK_RESUMING_LABEL,
  },
};

/**
 * The continuation for a daemon error code, or undefined if that code is not a
 * guard stop. `hasOwnProperty` rather than a bare index so a code like
 * "constructor" cannot walk the prototype into a truthy answer.
 */
export function guardStopContinuation(code: string | undefined): GuardStopContinuation | undefined {
  if (!code) return undefined;
  return Object.prototype.hasOwnProperty.call(GUARD_STOP_CONTINUATIONS, code)
    ? GUARD_STOP_CONTINUATIONS[code]
    : undefined;
}

/** Every continuation message, for recognising one that came back from the DB. */
const GUARD_CONTINUE_MESSAGES: ReadonlySet<string> = new Set(
  Object.values(GUARD_STOP_CONTINUATIONS).map((c) => c.message),
);
// Backoff before each automatic resume, indexed by attempt-1 (attempt 1 → 800ms,
// attempt 2 → 1600ms). A short, growing delay lets a transient network / proxy
// hiccup clear without hammering the daemon.
const AUTO_CONTINUE_BACKOFF_MS = [800, 1600] as const;

// Error codes that end a turn abnormally but must NEVER trigger an automatic
// "continue". `credits_exhausted` is the money-safety case: the daemon emits it
// only AFTER flipping to the personal OAuth fallback, so a silent resend would
// spend Patrik's own credits. Spend caps, in-flight/capacity (they self-resolve;
// resending is pointless), and a dead conversation round it out. Auth and 403
// are handled by their own flows and never reach this classifier.
//
// A deliberately stopped turn belongs here twice over: `deep_mode_limit` and
// `subagent_write_blocked` are also `retryable: false` in friendlyError, and
// this set is the second, independent brake. Resending re-runs the same fan-out
// into the same guard, which is a loop that spends on every lap.
//
// BOTH stay in this set even though each now gets ONE automatic continuation.
// That continuation is a different mechanism with a different message, its own
// single-shot latch, and no share of the MAX_AUTO_CONTINUE budget. Removing
// either code from here would hand the generic chain two more unsteered
// resends on top of it, which is the spend loop this set exists to prevent.
// Every key of GUARD_STOP_CONTINUATIONS must appear here.
//
// `context_exhausted` is that same shape without the spend angle: the
// conversation itself is over the ceiling, so an automatic resend re-enters the
// identical oversized context and fails identically, minutes later, for nothing.
/**
 * What the member reads when a queued prompt's edit was refused 409: the
 * daemon's `reason` says whether the prompt already started or another tab
 * changed it first. Either way the typed edit is gone, so it is said out loud.
 */
function editLostInfo(reason: string | undefined): FriendlyError {
  return {
    code: "in_flight",
    title: "Edit not saved",
    body: reason === "changed"
      ? "This prompt was changed in another tab, so your edit was not saved. The latest version is shown."
      : "This prompt had already started, so your edit was not saved.",
    severity: "info",
    retryable: false,
  };
}

export const NO_AUTO_CONTINUE_CODES: ReadonlySet<string> = new Set([
  "credits_exhausted",
  "deep_mode_limit",
  "subagent_write_blocked",
  "subagent_guard_failed",
  "context_exhausted",
  "daily_cap",
  "monthly_cap",
  "in_flight",
  "at_capacity",
  "unknown_conversation",
  // The daemon answers this BEFORE it records the user turn (src/server.ts
  // releases the claim and returns without ever reaching recordUserTurn), so
  // this conversation has no stored message for an automatic "continue" to
  // resume. Auto-continuing would send whatever text the hook happened to be
  // holding, which is the wrong text.
  "spawn_timeout",
  // The server-owned queue's refusals. None of them is changed by resending:
  // a full queue, an oversized prompt, too many open waits, spend another
  // running turn has reserved (429 on /chat, answered before the turn is
  // recorded), and a Resume refused by an account-wide wall.
  "queue_full",
  "message_too_large",
  "too_many_waiters",
  "spend_reserved",
  "account_paused",
]);

/**
 * Whether an error that ended a turn (by machine code and/or raw message) should
 * be auto-continued. Transient/retryable failures — network, timeout,
 * overloaded, rate_limited, server_error, worktree_failed, and the generic
 * unknown/connection-drop — qualify; NO_AUTO_CONTINUE_CODES and anything
 * friendlyError marks non-retryable do not.
 */
function isRetryableTurnEnd(code: string | undefined, rawMessage: string | undefined): boolean {
  if (code && NO_AUTO_CONTINUE_CODES.has(code)) return false;
  if (code && AUTH_ERROR_MARKERS.has(code)) return false;
  return friendlyError({ code, rawMessage }).retryable;
}

/** How a turn ended, driving post-turn orchestration (auto-continue / queue). */
type TurnOutcome = "incomplete" | "natural" | "abnormal" | "stopped" | "auth" | "forbidden" | "error_final";

/** Options for a single turn. `auto` marks an automatic stall-resume. */
type RunTurnOpts = { auto: boolean; compact?: boolean };

function nextId(): string {
  return `${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 8)}`;
}

function promptFromWire(conversationId: number, w: QueueItemWire): QueuedPrompt {
  return {
    id: w.client_id ?? `s-${w.id}`,
    serverId: w.id,
    text: w.text,
    attachments: w.attachments.map((m: AttachmentMeta) => storedAttachment(conversationId, m)),
  };
}

/**
 * The server's copy of a prompt the column already shows. The member's own
 * Attachment objects and ids are KEPT and only gain a `storedId`: the Composer
 * compares attachment lists by id, so swapping in server ids would make an
 * open edit read as modified and re-send every image. Returns `local` itself
 * when nothing changed, so React sees no new row.
 */
function reconcilePrompt(conversationId: number, w: QueueItemWire, local: QueuedPrompt | undefined): QueuedPrompt {
  if (!local) return promptFromWire(conversationId, w);
  const attachments =
    mergeStoredIds(local.attachments, w.attachments) ??
    w.attachments.map((m) => storedAttachment(conversationId, m));
  const sameAttachments =
    attachments.length === local.attachments.length && attachments.every((a, i) => a === local.attachments[i]);
  if (sameAttachments && local.text === w.text && local.serverId === w.id && local.status === undefined) return local;
  return { id: local.id, serverId: w.id, text: w.text, attachments };
}

/** An edit's images: stored ones by id (`keep`), new ones as bytes. */
function splitForEdit(attachments: Attachment[]): { keep: string[]; images: OutgoingImage[] } {
  const keep: string[] = [];
  const images: OutgoingImage[] = [];
  for (const a of attachments) {
    if (a.storedId !== undefined) keep.push(a.storedId);
    else images.push(toOutgoing(a));
  }
  return { keep, images };
}

function isAbort(e: unknown): boolean {
  return e instanceof Error && e.name === "AbortError";
}

/**
 * Tell the sidebar (ConversationDrawer) that a conversation in `project` just
 * gained activity, so it re-fetches and shows the new/updated name live without
 * a page reload. Uses the same window event-bus pattern as
 * `thunderay-ai:token-changed`. Guarded so non-browser test envs no-op.
 */
function notifyConversationActivity(project: string): void {
  if (typeof window === "undefined") return;
  try {
    window.dispatchEvent(new CustomEvent("thunderay-ai:conversation-activity", { detail: { project } }));
  } catch {
    /* restricted environment (about:blank, SSR) — ignore */
  }
}

/**
 * The message state a `TurnStatus` implies, in ONE place.
 *
 * A turn reaches the transcript by two routes that must agree: loaded from
 * history (`turnsToMessages`) and restored from a live `snapshot` frame. Both
 * describe the same row, so a turn the daemon is still writing has to render
 * live either way, and one orphaned by a restart has to render honestly as
 * interrupted either way. Mapping it twice is how those two renderings drift.
 *
 * `undefined` is accepted because the type is a promise the wire does not keep:
 * `TurnRow.status` is declared required (the column is NOT NULL with a
 * 'complete' default, so every row the current daemon selects has one), but a
 * response from a daemon predating the column carries no `status` at all. That
 * row must keep rendering exactly as it did before this field existed —
 * finished, not spuriously live — so the comparison is written to be false for
 * a missing value rather than to assume one.
 */
function statusFields(status: TurnStatus | undefined): { streaming: boolean; interrupted?: true } {
  return {
    streaming: status === "streaming",
    ...(status === "interrupted" ? { interrupted: true as const } : {}),
  };
}

function turnsToMessages(turns: TurnRow[]): ChatMessage[] {
  return turns.map((t) => {
    // The conversation id comes off the row rather than from the caller: the
    // three history paths would each have to pass it, and one of them getting it
    // wrong would point every image on the turn at another conversation. The
    // guard is because `attachments` is whatever the daemon actually sent —
    // AttachmentMeta is declared once here and once there with nothing checking
    // the two agree, so a shape change lands at runtime, not at compile time.
    const stored = Array.isArray(t.attachments) ? t.attachments : [];
    return {
      id: `t-${t.id}`,
      role: t.role,
      content: t.content,
      // An automatic continuation is a real user turn on the server, so after a
      // reload (and for anyone else opening the conversation) it would come back
      // as an ordinary bubble in the member's own name. Recognise the exact text
      // of ANY guard continuation and restore the compact chip, so what the
      // member typed and what the app sent on their behalf stay visually
      // distinct. Matching on the literals is safe because the constants are
      // the only thing that ever produces them, and a member who typed one by
      // hand gets the chip too, which is harmless.
      ...(t.role === "user" && GUARD_CONTINUE_MESSAGES.has(t.content) ? { autoContinue: true as const } : {}),
      ...statusFields(t.status),
      ...(stored.length > 0
        ? { attachments: stored.map((meta) => ({ kind: "stored" as const, conversationId: t.conversation_id, meta })) }
        : {}),
    };
  });
}

/**
 * Put a reloaded transcript on screen WITHOUT remounting what is already
 * there: every message that still matches its row (same position, same role,
 * finished) is kept as the same object, and only the rows from the first
 * mismatch on come from `turns`. The LAST row always comes from `turns`,
 * because a reattach addresses it as `t-<id>`.
 */
function mergeTurnsIntoMessages(prev: ChatMessage[], turns: TurnRow[]): ChatMessage[] {
  const next = turnsToMessages(turns);
  // A kept row must be the SAME row, not merely the same role: a local-only
  // exchange (a prompt the daemon refused, 429 in_flight) sits where the
  // server has a different prompt, and keeping it would show the wrong text
  // above the queue turn. A row that already came from the server (`t-<id>`)
  // must also keep its id.
  const same = (a: ChatMessage, b: ChatMessage): boolean =>
    a.role === b.role && !a.streaming && a.content === b.content && (!a.id.startsWith("t-") || a.id === b.id);
  let k = 0;
  while (k < prev.length && k < next.length - 1 && same(prev[k]!, next[k]!)) k++;
  return [...prev.slice(0, k), ...next.slice(k)];
}

/** Whether a queue call failed with a refusal (4xx) rather than an unknown outcome (network, 5xx). */
function isRefusal(e: unknown): e is HttpError {
  return e instanceof HttpError && e.status >= 400 && e.status < 500;
}

/** A wait that an abort ends early; resolves either way. */
function sleep(ms: number, signal: AbortSignal): Promise<void> {
  return new Promise((resolve) => {
    if (signal.aborted) { resolve(); return; }
    const t = setTimeout(resolve, ms);
    signal.addEventListener("abort", () => { clearTimeout(t); resolve(); }, { once: true });
  });
}

function classifyError(e: unknown): { isAuth: boolean; err: Error } {
  const err = e instanceof Error ? e : new Error(String(e));
  // 401-level HttpError counts as auth too, but for back-compat we also keep
  // the message-marker test so non-HttpError "unauthorized" surfaces are caught.
  const isHttp401 = err instanceof HttpError && err.status === 401;
  const isAuth = isHttp401 || AUTH_ERROR_MARKERS.has(err.message);
  return { isAuth, err };
}

/** Build the rendered progress entry for one wire frame. Shared by the live
 *  `progress` branch and by every row restored from a `snapshot`, which is only
 *  safe because the two are byte-for-byte the same shape: the API layer stamps
 *  `type: "progress"` on a restored row and validates its `kind` against the
 *  same registry the live path uses (src/api/sse.ts parseProgress). */
function toProgressEntry(ev: ProgressEvent): ProgressEntry {
  const meta = ev.meta ?? {};
  return {
    id: nextId(),
    kind: ev.kind,
    label: ev.label,
    ts: Date.now(),
    // Carry deep-mode attribution through so the UI can group/label by agent.
    // Missing/unknown fields render exactly as a normal entry.
    ...(typeof meta.agentId === "string" ? { agentId: meta.agentId } : {}),
    ...(typeof meta.agentType === "string" ? { agentType: meta.agentType } : {}),
    ...(meta.agentSpawn === true ? { agentSpawn: true } : {}),
  };
}

/**
 * Bound a progress trail to MAX_PROGRESS_ENTRIES, dropping from the FRONT
 * (oldest) and tallying what it dropped. Keeps the per-frame copy O(K) instead
 * of O(n) and caps the rendered DOM.
 *
 * Exempt kinds are SKIPPED rather than dropped, so the scan walks past them to
 * the oldest evictable entry. Live frames arrive one at a time, so this
 * normally removes exactly one element; the walk is bounded by the handful of
 * exempt entries a turn can produce, and a trail made entirely of them simply
 * stays whole.
 *
 * MUTATES `next` in place — every caller hands over an array it just built, so
 * bounding it costs no extra copy on the hot path the cap exists to protect.
 */
function capProgress(next: ProgressEntry[], overflow: number): { progress: ProgressEntry[]; progressOverflow: number } {
  let dropped = overflow;
  let scan = 0;
  while (next.length > MAX_PROGRESS_ENTRIES && scan < next.length) {
    if (PROGRESS_EVICTION_EXEMPT.has(next[scan]!.kind)) { scan++; continue; }
    next.splice(scan, 1);
    dropped += 1;
  }
  return { progress: next, progressOverflow: dropped };
}

/**
 * Everything `applyFrame` needs that is specific to one turn. The message
 * mutations are shared; the turn-lifecycle decisions (outcome tracking,
 * auto-continue, the queue) stay with the caller behind `onDone`/`onError`,
 * because those differ between a turn this client started and one it merely
 * reattached to.
 */
type FrameCtx = {
  assistantId: string;
  superseded: () => boolean;
  setMessages: Dispatch<SetStateAction<ChatMessage[]>>;
  onDone: (subtype?: string, nextItemId?: number) => void;
  onError: (error: string, code?: string) => void;
  /** Every snapshot: the daemon names the turn and the conversation there. */
  onSnapshot?: (ev: SnapshotEvent) => void;
};

/**
 * Apply ONE stream frame to the transcript.
 *
 * Extracted so `send()` and the reattach path run the same reducer. They
 * consume the same frames from the same daemon, and the moment each one owns a
 * copy of this switch they start disagreeing about what a frame means — with
 * the reattach copy exercised only by whoever happens to reload mid-turn.
 */
function applyFrame(ev: StreamEvent, ctx: FrameCtx): void {
  // A superseded turn (Stop, a newer turn, unmount, conversation switch) is
  // inert: its remaining buffered frames must not write into a transcript that
  // has already moved on, or append tokens under a bubble Stop just finalized.
  if (ctx.superseded()) return;

  if (ev.type === "snapshot") {
    // REPLACES. It never appends, and this is the one line in the hook that
    // corrupts the answer on screen if it is got wrong. The snapshot is the
    // server's whole truth for this turn so far: after the daemon produced
    // "AAA" then "BBB", a newly attached client's snapshot is "AAABBB", the
    // entire answer rather than the part it missed. A consumer that appends
    // prints the part it already had twice.
    //
    // The progress trail is replaced for the same reason, not appended: a
    // second snapshot (reattaching to a turn already reattached to) would
    // otherwise be correct in the text and doubled in the steps — idempotent
    // in half the frame, which is worse than either. Restored rows go through
    // the SAME cap as live ones, because a long deep-mode snapshot carries the
    // whole trail in a single frame and that is exactly the size the cap
    // exists to bound.
    const restored = capProgress(ev.progress.map(toProgressEntry), 0);
    ctx.setMessages((prev) =>
      prev.map((m) => (m.id === ctx.assistantId
        ? { ...m, content: ev.content, ...statusFields(ev.status), ...restored }
        : m)),
    );
    ctx.onSnapshot?.(ev);
    return;
  }

  if (ev.type === "token") {
    ctx.setMessages((prev) =>
      prev.map((m) => (m.id === ctx.assistantId ? { ...m, content: m.content + ev.text } : m)),
    );
    return;
  }

  if (ev.type === "progress") {
    const entry = toProgressEntry(ev);
    ctx.setMessages((prev) =>
      prev.map((m) => (m.id === ctx.assistantId
        ? { ...m, ...capProgress([...(m.progress ?? []), entry], m.progressOverflow ?? 0) }
        : m)),
    );
    return;
  }

  if (ev.type === "done") {
    ctx.onDone(ev.subtype, ev.queueNext ? ev.nextItemId : undefined);
    return;
  }

  ctx.onError(ev.error, ev.code);
}

export function useChatStream(project: string, conversationId: number | null = null): UseChatStream {
  const [messages, setMessages] = useState<ChatMessage[]>([]);
  const [isStreaming, setIsStreaming] = useState(false);
  const [isLoadingHistory, setIsLoadingHistory] = useState(false);
  const [authError, setAuthError] = useState(false);
  const [error, setError] = useState<Error | null>(null);
  const [errorInfo, setErrorInfo] = useState<FriendlyError | null>(null);
  const [currentConversationId, setCurrentConversationId] = useState<number | null>(conversationId);
  const [queue, setQueue] = useState<QueuedPrompt[]>([]);
  const [isResuming, setIsResuming] = useState(false);
  const stopRequestedRef = useRef(false);
  // Mirror of `isStreaming` readable synchronously from any callback/closure, so
  // the enqueue-vs-run decision and the auto-continue timer never race a stale
  // React state snapshot.
  const isStreamingRef = useRef(false);
  // Authoritative queue mirror: mutated synchronously alongside setQueue so
  // advance/enqueue read the live queue without waiting for a re-render.
  const queueRef = useRef<QueuedPrompt[]>([]);
  const [queuePause, setQueuePause] = useState<{ reason: string | null } | null>(null);
  const pausedRef = useRef(false);
  // True while an automatic continuation that carries resumeQueue is pending
  // or running: the server's pause is about to be cleared by it, so the
  // paused banner and its Resume button stay hidden meanwhile.
  const [queueResuming, setQueueResuming] = useState(false);
  // The conversation the queue (and the turns on screen) belong to. Every
  // queue call is bound to it; a response for another conversation is dropped.
  const queueConvRef = useRef<number | null>(conversationId);
  // Bumped on every local reset (a switch, a new chat): a queue call from
  // before the reset must not write into the queue that replaced it.
  const queueGenRef = useRef(0);
  // POSTs of prompts still "sending", by client id: an edit, move or remove of
  // such a prompt waits for its server id.
  const pendingPostsRef = useRef(new Map<string, Promise<number | null>>());
  // Prompts removed while their POST was in flight: deleted when it lands.
  const cancelledPostsRef = useRef(new Set<string>());
  // Server ids the queue already started. A POST answer that arrives after the
  // start must not put its prompt back as a chip.
  const handedOnIdsRef = useRef(new Set<number>());
  // Prompts THIS column enqueued, by client id and by server id. A queue turn
  // made from one of them is the column's own (orchestrated: auto-continue,
  // guard continuation, chime); any other queue turn is only watched.
  const ownClientIdsRef = useRef(new Set<string>());
  const ownServerIdsRef = useRef(new Set<number>());
  // Queue refreshes are numbered; an answer older than one already applied is
  // dropped, so a slow GET cannot put back what a newer one removed.
  const refreshSeqRef = useRef(0);
  const refreshAppliedRef = useRef(0);
  // Whether a follow of the queue set isStreaming. A follow that supersedes
  // another inherits this, so the streaming state is always released by
  // whichever follow ends last.
  const followHoldsStreamRef = useRef(false);
  // Prompts queued while a conversation-less turn runs wait here for the
  // conversation its snapshot names, instead of opening a second one.
  const convWaitersRef = useRef<Array<(id: number | null) => void>>([]);
  // The assistant turn this column is watching, for Stop. Set by a snapshot or
  // by the queue's start answer; cleared the moment a turn ends, so a Stop in
  // the gap before the next turn never names the finished one.
  const watchedTurnIdRef = useRef<number | null>(null);
  // Whether an automatic continuation of the current chain may resume the
  // queue: true when the chain's first turn began with the queue unpaused, or
  // is a queue turn this column owns. A prompt typed while the queue is paused
  // runs now, and its stall must not silently resume the paused queue.
  const chainResumesQueueRef = useRef(false);
  // Whether the turn that just ended was sent with resumeQueue.
  const lastTurnResumedQueueRef = useRef(false);
  // The GET .../queue/next wait; aborted on a switch, on Stop and on unmount.
  const queueWaitAbortRef = useRef<AbortController | null>(null);
  // Defined after reattachIfLive; reached through refs like runTurnRef.
  const followQueueStartRef = useRef<
    ((convId: number, nextItemId: number | null, predecessorOwned?: boolean, holdStream?: boolean) => Promise<void>) | null
  >(null);
  const reloadTurnsRef = useRef<((convId: number, ownUserTurnId?: number) => Promise<void>) | null>(null);
  // Per-turn generation id + the current turn's AbortController. A turn whose id
  // no longer matches `runIdRef` has been SUPERSEDED (Stop, a newer turn,
  // unmount, or conversation change) and must become inert: it may not reset
  // shared streaming state, orchestrate, or schedule a resume.
  //
  // `abortRef` holds the controller for a turn THIS column started, and only
  // `stop()` fires it. Leaving — a conversation switch or an unmount — must
  // supersede without aborting: the turn belongs to the conversation now, not to
  // this column, and aborting reaches the daemon as a client disconnect. That
  // was the reported bug (switching chats stopped the stream); superseding alone
  // keeps the frames from writing into a transcript that has moved on.
  const runIdRef = useRef(0);
  const abortRef = useRef<AbortController | null>(null);
  // The reattach reader's controller, kept apart from `abortRef` precisely
  // BECAUSE its leave semantics are the opposite. A reattach is this column
  // watching someone else's turn, so leaving must hang up: only aborting the
  // fetch disconnects a reader, and every reader left attached costs the daemon
  // a socket plus a 15s heartbeat for as long as the turn runs.
  const reattachAbortRef = useRef<AbortController | null>(null);
  // Auto-continue chain state. `count` is how many automatic resumes have fired
  // in the current stall-chain; `pending` is true from scheduling a resume until
  // the chain resolves, and gives a live resume precedence over the queue;
  // `timer` holds the scheduled resume so Stop/unmount can cancel it.
  const autoContinueCountRef = useRef(0);
  const autoContinuePendingRef = useRef(false);
  const autoContinueTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  // Single-shot latch for the guard-stop continuation: set when one is
  // scheduled, cleared by any turn the USER started, which is a typed prompt, a
  // queued prompt, or a click on Continue (and by a conversation switch, which
  // resets everything). It is what bounds the continuation to ONE per user
  // action, so a task that keeps hitting a guard stops and shows the card
  // instead of continuing itself indefinitely.
  //
  // The Continue CLICK re-arms it deliberately. A click is a user action and
  // costs the member a deliberate decision, so the turn it starts is entitled
  // to the same single continuation a typed prompt gets. What that rules out is
  // only the unattended loop: nothing the app does on its own re-arms this.
  //
  // SHARED ACROSS GUARDS on purpose: one prompt that trips the budget stop and
  // then the write block has had its continuation, and a per-code latch would
  // hand it a second one, which is the self-continuing loop this bounds.
  const guardContinueUsedRef = useRef(false);
  // The guard code whose message the most recent AUTOMATIC turn carried, or null.
  // Distinct from guardContinueUsedRef, which only records that the one-shot was
  // spent: this says what the turn now finishing actually WAS, which is the only
  // way to recognise a continuation that died at the HTTP layer. Its own error
  // code is then `spawn_timeout` or `in_flight`, not a guard code, so nothing in
  // the failed turn itself remembers the steering message, and the original
  // guard-stop bubble was scrubbed when this turn started. Holds the CODE rather
  // than a boolean so the resend picks the message the stop actually earned.
  const guardContinueSentCodeRef = useRef<string | null>(null);
  /**
   * The text that should resume the turn now ending.
   *
   * A continuation that dies at the transport does NOT reliably end
   * non-retryable. `isRetryableTurnEnd` builds its verdict without the HTTP
   * status, so any daemon code the frontend has not mirrored derives as
   * `unknown` and comes back RETRYABLE, taking the generic stall chain (that
   * was `spawn_timeout` itself until it was mirrored as non-retryable); the
   * mirrored codes such as 409 `in_flight` end error_final. Both paths
   * therefore have to ask this question, or the retryable one quietly resends
   * a bare "continue" and the model launches the very panel that hit the guard.
   */
  const resumeText = (): string =>
    guardStopContinuation(guardContinueSentCodeRef.current ?? undefined)?.message ?? "continue";
  // Always-latest mirror of `messages`, so `continueTurn` can see how the turn
  // it is resuming actually ended without taking `messages` as a dependency and
  // re-creating the callback on every token. Assigned during render rather than
  // in an effect: the Continue button is a click, which cannot happen before the
  // render that drew it, so this is current by the time anything reads it.
  const messagesRef = useRef<ChatMessage[]>([]);
  messagesRef.current = messages;
  // Always-latest reference to the streaming routine, so timers, the queue, and
  // the Continue button drive the freshest closure without stale-capture bugs.
  const runTurnRef = useRef<((text: string, attachments: Attachment[], opts: RunTurnOpts) => Promise<void>) | null>(null);
  // Guards background revalidation: a stale-while-revalidate fetch that resolves
  // after the column closed must not call setState on an unmounted hook.
  const mountedRef = useRef(true);
  useEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
      // Supersede any in-flight turn so it cannot setState after unmount — but
      // do NOT abort it. Closing this column is the commonest way to "switch to
      // another chat" (ColumnLayout keys each column by conversation, so
      // pointing a slot at another conversation unmounts this one), and the turn
      // has to survive that: it is the daemon's now, and the user gets it back
      // by reopening the conversation. The reattach reader is the opposite case
      // and does hang up.
      runIdRef.current++;
      reattachAbortRef.current?.abort();
      queueWaitAbortRef.current?.abort();
      if (autoContinueTimerRef.current !== null) {
        clearTimeout(autoContinueTimerRef.current);
        autoContinueTimerRef.current = null;
      }
    };
  }, []);

  const clearAuthError = useCallback(() => setAuthError(false), []);
  const clearError = useCallback(() => {
    setError(null);
    setErrorInfo(null);
  }, []);

  const toFriendly = useCallback(
    (err: Error): FriendlyError =>
      friendlyError({
        status: err instanceof HttpError ? err.status : undefined,
        code: err instanceof HttpError ? err.code : undefined,
        rawMessage: err.message,
      }),
    [],
  );

  // ---- Prompt queue (server-owned) ---------------------------------------
  // The daemon owns and drains the queue; this mirrors it for the Composer and
  // sends the member's edits. Every mutation is optimistic and falls back to a
  // refetch when the server refuses it. Every write is bound to the queue
  // generation captured when the call began.

  const setLocalQueue = useCallback((next: QueuedPrompt[]) => {
    queueRef.current = next;
    setQueue(next);
  }, []);

  const setPaused = useCallback((p: { reason: string | null } | null) => {
    pausedRef.current = p !== null;
    setQueuePause(p);
  }, []);

  const settleConvWaiters = useCallback((id: number | null) => {
    const waiting = convWaitersRef.current;
    convWaitersRef.current = [];
    for (const resolve of waiting) resolve(id);
  }, []);

  /** Forget the local mirror (a switch, a new chat). Never touches the server. */
  const resetQueueLocal = useCallback(() => {
    queueGenRef.current++;
    queueWaitAbortRef.current?.abort();
    queueWaitAbortRef.current = null;
    pendingPostsRef.current.clear();
    cancelledPostsRef.current.clear();
    handedOnIdsRef.current.clear();
    ownClientIdsRef.current.clear();
    ownServerIdsRef.current.clear();
    followHoldsStreamRef.current = false;
    settleConvWaiters(null);
    setLocalQueue([]);
    setPaused(null);
    setQueueResuming(false);
  }, [setLocalQueue, setPaused, settleConvWaiters]);

  const patchLocal = useCallback((id: string, patch: (q: QueuedPrompt) => QueuedPrompt) => {
    setLocalQueue(queueRef.current.map((q) => (q.id === id ? patch(q) : q)));
  }, [setLocalQueue]);

  /** A prompt the server started: gone from the mirror, and never re-added by its late POST answer. */
  const dropHandedOn = useCallback((serverId: number) => {
    handedOnIdsRef.current.add(serverId);
    setLocalQueue(queueRef.current.filter((q) => q.serverId !== serverId));
  }, [setLocalQueue]);

  const applyServerQueue = useCallback((convId: number, state: QueueState) => {
    if (queueConvRef.current !== convId) return;
    const current = queueRef.current;
    for (const w of state.items) {
      if (w.client_id !== null && ownClientIdsRef.current.has(w.client_id)) ownServerIdsRef.current.add(w.id);
    }
    // A prompt the queue already started, or one this column is deleting, is
    // not a chip whatever an answer read before that says.
    const live = state.items.filter(
      (w) => !handedOnIdsRef.current.has(w.id) && !(w.client_id !== null && cancelledPostsRef.current.has(w.client_id)),
    );
    const saved = live.map((w) =>
      reconcilePrompt(
        convId,
        w,
        current.find((p) => p.id === (w.client_id ?? `s-${w.id}`)) ?? current.find((p) => p.serverId === w.id),
      ),
    );
    const savedIds = new Set(saved.map((p) => p.id));
    // Prompts the server does not have (sending, or refused) stay, at the end.
    const local = current.filter((p) => p.status !== undefined && !savedIds.has(p.id));
    setLocalQueue([...saved, ...local]);
    setPaused(state.paused ? { reason: state.pausedReason } : null);
  }, [setLocalQueue, setPaused]);

  const refreshQueue = useCallback(async (convId: number): Promise<QueueState | null> => {
    const gen = queueGenRef.current;
    const seq = ++refreshSeqRef.current;
    try {
      const state = await getQueue(project, convId);
      if (gen !== queueGenRef.current || !mountedRef.current) return null;
      // A newer answer already landed: this one is out of date. It still
      // answers the caller, whose decision a newer state does not change.
      if (seq > refreshAppliedRef.current) {
        refreshAppliedRef.current = seq;
        applyServerQueue(convId, state);
      }
      return state;
    } catch (e) {
      if (gen === queueGenRef.current && mountedRef.current && classifyError(e).isAuth) setAuthError(true);
      return null;
    }
  }, [project, applyServerQueue]);

  /**
   * The conversation a new queued prompt belongs to. Normally known (loaded,
   * or named by the running turn's snapshot). While a conversation-less turn
   * streams, its first frame is about to name the conversation: wait for that
   * rather than open a second one. Only when there is no turn to name one is a
   * conversation created. Null when the queue was reset meanwhile.
   */
  const targetConversation = useCallback(async (gen: number): Promise<number | null> => {
    if (queueConvRef.current !== null) return queueConvRef.current;
    if (isStreamingRef.current) {
      const named = await new Promise<number | null>((resolve) => { convWaitersRef.current.push(resolve); });
      if (gen !== queueGenRef.current) return null;
      if (named !== null) return named;
      if (queueConvRef.current !== null) return queueConvRef.current;
    }
    try {
      const row = await createConversation(project);
      if (gen !== queueGenRef.current) return null;
      if (queueConvRef.current === null) {
        queueConvRef.current = row.id;
        setCurrentConversationId(row.id);
      }
      return queueConvRef.current;
    } catch (e) {
      if (gen === queueGenRef.current && classifyError(e).isAuth) setAuthError(true);
      return null;
    }
  }, [project]);

  /**
   * The server saved `p` as `item`: make the chip that row. `sent` is what the
   * POST carried. A repeated POST (a Retry, or a reconcile after a lost
   * answer) is answered with the row as it was first saved, so an edit made
   * while the chip was unsaved is PATCHed on now. `nextItemId` is a start the
   * enqueue itself caused (the daemon's kick).
   */
  const adoptSaved = useCallback(
    (convId: number, gen: number, p: QueuedPrompt, item: QueueItemWire, sent: QueuedPrompt, nextItemId: number | null): number | null => {
      ownServerIdsRef.current.add(item.id);
      if (cancelledPostsRef.current.delete(p.id)) {
        // Removed while on its way: remove it on the server too.
        void deleteQueued(project, convId, item.id)
          .catch((e: unknown) => {
            if (gen !== queueGenRef.current) return;
            if (classifyError(e).isAuth) setAuthError(true);
          })
          .finally(() => { if (gen === queueGenRef.current) void refreshQueue(convId); });
        return null;
      }
      if (handedOnIdsRef.current.has(item.id)) {
        // The queue started it before this answer arrived: no ghost chip.
        setLocalQueue(queueRef.current.filter((q) => q.id !== p.id));
        return null;
      }
      // A repeated POST answers with the row as first saved: when that differs
      // from what was sent, it is an OLD copy, and its images are the wrong
      // ones (their ids may already be gone). The chip then keeps what the
      // member sent and takes the stored ids from the PATCH's answer below, so
      // a later edit that keeps an image names an id the server has.
      const differs = item.text !== sent.text.trim() || item.attachments.length !== sent.attachments.length;
      // Keep the member's text, objects and ids; take only the server id and
      // the stored ids. Edited meanwhile: the edit's PATCH carries the rest.
      patchLocal(p.id, (q) => ({
        ...q,
        serverId: item.id,
        status: undefined,
        attachments:
          q.attachments === sent.attachments && !differs
            ? mergeStoredIds(q.attachments, item.attachments) ??
              item.attachments.map((m) => storedAttachment(convId, m))
            : q.attachments,
      }));
      if (differs) {
        void (async () => {
          try {
            const images = (await rehydrateStoredAttachments(sent.attachments)).map(toOutgoing);
            const saved = await editQueued(project, convId, item.id, { message: sent.text, keep: [], images });
            if (gen !== queueGenRef.current) return;
            patchLocal(p.id, (q) => {
              if (q.attachments !== sent.attachments) return q;
              const merged = mergeStoredIds(sent.attachments.map(({ storedId: _old, ...a }) => a), saved.attachments);
              return merged ? { ...q, attachments: merged } : q;
            });
          } catch (e) {
            if (gen !== queueGenRef.current) return;
            if (classifyError(e).isAuth) setAuthError(true);
            void refreshQueue(convId);
          }
        })();
      }
      if (nextItemId !== null) void followQueueStartRef.current?.(convId, nextItemId, true);
      return item.id;
    },
    [project, patchLocal, setLocalQueue, refreshQueue],
  );

  /**
   * Act on an enqueue answer, first or repeated. `consumed`: the prompt
   * already ran, so it is a turn now, not a chip; show the transcript that
   * holds it. `starting`: the drain holds it (GET queue does not list a row in
   * transit), so drop the chip and follow that start as this column's own.
   * Otherwise the server saved it as `item`.
   */
  const applyEnqueue = useCallback(
    (convId: number, gen: number, p: QueuedPrompt, sent: QueuedPrompt, res: EnqueueResult): number | null => {
      if (res.item !== null) return adoptSaved(convId, gen, p, res.item, sent, res.started ? res.nextItemId : null);
      cancelledPostsRef.current.delete(p.id);
      setLocalQueue(queueRef.current.filter((q) => q.id !== p.id));
      if ("starting" in res) {
        ownServerIdsRef.current.add(res.starting.itemId);
        void followQueueStartRef.current?.(convId, res.starting.itemId);
        return null;
      }
      void reloadTurnsRef.current?.(convId, res.consumed.turnId);
      return null;
    },
    [adoptSaved, setLocalQueue],
  );

  const postQueued = useCallback((p: QueuedPrompt): Promise<number | null> => {
    const gen = queueGenRef.current;
    const run: Promise<number | null> = (async () => {
      const convId = await targetConversation(gen);
      if (gen !== queueGenRef.current) return null;
      // Removed (or cleared) while it waited for a conversation: never sent.
      if (cancelledPostsRef.current.delete(p.id)) return null;
      if (convId === null) {
        patchLocal(p.id, (q) => ({ ...q, status: "failed" }));
        return null;
      }
      // What the member sees NOW: an edit made while this waited for a
      // conversation goes out with the POST.
      const sent = queueRef.current.find((q) => q.id === p.id) ?? p;
      // Kept for the reconcile's re-POST, which must carry the same prompt.
      let images: OutgoingImage[] | null = null;
      try {
        // A draft carried over from a queued prompt may hold stored images by
        // reference only (empty base64): load their bytes first. This throws
        // rather than send an empty image, and the chip then reads "failed".
        images = (await rehydrateStoredAttachments(sent.attachments)).map(toOutgoing);
        const res = await enqueuePrompt(project, convId, { message: sent.text, images, clientId: p.id });
        if (gen !== queueGenRef.current) return null;
        return applyEnqueue(convId, gen, p, sent, res);
      } catch (e) {
        if (gen !== queueGenRef.current) return null;
        if (classifyError(e).isAuth) setAuthError(true);
        if (!isRefusal(e)) {
          // The outcome is unknown (the answer was lost, or a 5xx): the server
          // may have saved the prompt, and even started one. Ask before
          // calling it failed.
          const state = await refreshQueue(convId);
          if (gen !== queueGenRef.current) return null;
          // Something is starting that nothing here follows (the enqueue's own
          // kick, whose answer was lost): follow it.
          if (state?.running && queueWaitAbortRef.current === null && !isStreamingRef.current) {
            void followQueueStartRef.current?.(convId, null);
          }
          const row = state?.items.find((w) => w.client_id === p.id);
          if (row) return adoptSaved(convId, gen, p, row, sent, null);
          // Not listed: it may be `starting` (GET queue omits a row in
          // transit), already ran, or never saved. The same client id is
          // idempotent, so ask again: the answer says which, and a prompt that
          // was never saved is saved now.
          if (images !== null) {
            try {
              const again = await enqueuePrompt(project, convId, { message: sent.text, images, clientId: p.id });
              if (gen !== queueGenRef.current) return null;
              return applyEnqueue(convId, gen, p, sent, again);
            } catch (e2) {
              if (gen !== queueGenRef.current) return null;
              if (classifyError(e2).isAuth) setAuthError(true);
              else if (isRefusal(e2)) { setError(e2); setErrorInfo(toFriendly(e2)); }
            }
          }
        } else if (!classifyError(e).isAuth) {
          // A definite refusal (a full queue, a prompt too large, a chat that
          // was archived): say why, since Retry alone will not change it.
          setError(e);
          setErrorInfo(toFriendly(e));
        }
        if (cancelledPostsRef.current.delete(p.id)) return null;
        patchLocal(p.id, (q) => ({ ...q, status: "failed" }));
        return null;
      }
    })();
    pendingPostsRef.current.set(p.id, run);
    // `run` never rejects (every path above returns), so this cleanup cannot
    // surface an unhandled rejection. It cannot live in a `finally` inside the
    // IIFE: `run` is not assigned yet there (TS2454).
    void run.finally(() => {
      if (pendingPostsRef.current.get(p.id) === run) pendingPostsRef.current.delete(p.id);
    });
    return run;
  }, [project, targetConversation, patchLocal, setLocalQueue, refreshQueue, adoptSaved, applyEnqueue, toFriendly]);

  const enqueue = useCallback((text: string, attachments: Attachment[] = []) => {
    const p: QueuedPrompt = { id: nextId(), text, attachments, status: "sending" };
    ownClientIdsRef.current.add(p.id);
    setLocalQueue([...queueRef.current, p]);
    void postQueued(p);
  }, [setLocalQueue, postQueued]);

  const retryQueued = useCallback((id: string) => {
    const p = queueRef.current.find((q) => q.id === id);
    if (!p || p.status !== "failed") return;
    const sending: QueuedPrompt = { ...p, status: "sending" };
    patchLocal(id, () => sending);
    void postQueued(sending);
  }, [patchLocal, postQueued]);

  /** The server id of a prompt, waiting for its POST if it is still sending. */
  const serverIdOf = useCallback(async (p: QueuedPrompt): Promise<number | null> => {
    if (p.serverId !== undefined) return p.serverId;
    return (await pendingPostsRef.current.get(p.id)) ?? null;
  }, []);

  const removeQueued = useCallback((id: string) => {
    const p = queueRef.current.find((q) => q.id === id);
    if (!p) return;
    setLocalQueue(queueRef.current.filter((q) => q.id !== id));
    if (p.status === "sending") {
      // Its POST deletes it when it lands, or is never sent. Known conversation
      // or not: the pending POST is the one place that can act on it.
      cancelledPostsRef.current.add(p.id);
      return;
    }
    const convId = queueConvRef.current;
    if (convId === null) return;
    const gen = queueGenRef.current;
    if (p.serverId === undefined) {
      // A failed chip. Its POST may have been saved with the answer lost: find
      // it by client id and delete it, or it would still run.
      cancelledPostsRef.current.add(p.id);
      void (async () => {
        try {
          const state = await getQueue(project, convId);
          if (gen !== queueGenRef.current) return;
          const row = state.items.find((w) => w.client_id === p.id);
          if (row) await deleteQueued(project, convId, row.id);
        } catch (e) {
          if (gen === queueGenRef.current && classifyError(e).isAuth) setAuthError(true);
        } finally {
          if (gen === queueGenRef.current) {
            cancelledPostsRef.current.delete(p.id);
            void refreshQueue(convId);
          }
        }
      })();
      return;
    }
    void deleteQueued(project, convId, p.serverId).catch((e: unknown) => {
      if (gen !== queueGenRef.current) return;
      if (classifyError(e).isAuth) setAuthError(true);
      void refreshQueue(convId);
    });
  }, [project, setLocalQueue, refreshQueue]);

  const updateQueued = useCallback((id: string, text: string, attachments: Attachment[] = []) => {
    // Hold send's non-empty invariant: a prompt empty of BOTH text and images
    // is refused. An image with no caption is a real prompt.
    if (!text.trim() && attachments.length === 0) return;
    const p = queueRef.current.find((q) => q.id === id);
    if (!p) return;
    patchLocal(id, (q) => ({ ...q, text, attachments }));
    // A failed chip is saved by Retry, which PATCHes the server's copy if one
    // was saved after all.
    if (p.status === "failed") return;
    const gen = queueGenRef.current;
    void (async () => {
      // A sending chip waits for its POST, conversation id or not.
      const serverId = await serverIdOf(p);
      const convId = queueConvRef.current;
      if (serverId === null || convId === null || gen !== queueGenRef.current) return;
      try {
        const item = await editQueued(project, convId, serverId, { message: text, ...splitForEdit(attachments) });
        if (gen !== queueGenRef.current) return;
        // Same objects and ids as the member sees (the Composer compares by
        // id); only the stored ids are added. A newer edit meanwhile wins.
        patchLocal(id, (q) =>
          q.text === text && q.attachments === attachments
            ? {
                ...q,
                serverId: item.id,
                attachments:
                  mergeStoredIds(q.attachments, item.attachments) ??
                  item.attachments.map((m) => storedAttachment(convId, m)),
              }
            : q,
        );
      } catch (e) {
        if (gen !== queueGenRef.current) return;
        const { isAuth } = classifyError(e);
        if (isAuth) setAuthError(true);
        // A refused edit rolls back below. When the refusal is about the
        // content (too large, a bad image), say so. A 409 says the edit was
        // lost and why (the prompt started, or another tab changed it first):
        // otherwise the member's typing just vanishes. A prompt that went away
        // (404) explains itself by leaving the queue.
        if (!isAuth && isRefusal(e) && e.code === "in_flight") {
          setErrorInfo(editLostInfo(e.reason));
        } else if (!isAuth && isRefusal(e) && e.code !== "unknown_conversation") {
          setError(e);
          setErrorInfo(toFriendly(e));
        }
        void refreshQueue(convId);
      }
    })();
  }, [project, patchLocal, serverIdOf, refreshQueue, toFriendly]);

  const moveQueued = useCallback((id: string, toIndex: number): boolean => {
    // splice would coerce NaN to 0 and truncate a fraction: refuse both.
    if (!Number.isInteger(toIndex)) return false;
    const current = queueRef.current;
    const from = current.findIndex((q) => q.id === id);
    if (from === -1) return false;
    const to = Math.max(0, Math.min(current.length - 1, toIndex));
    if (to === from) return false;
    const next = current.slice();
    const [item] = next.splice(from, 1);
    next.splice(to, 0, item!);
    setLocalQueue(next);
    const convId = queueConvRef.current;
    if (convId === null || item!.status === "failed") return true;
    const gen = queueGenRef.current;
    void (async () => {
      const serverId = await serverIdOf(item!);
      if (serverId === null || gen !== queueGenRef.current) return;
      // The server orders only the prompts it has: count those ahead of this
      // one. A chip still sending has no place there yet (its own move, once
      // saved, puts it where it belongs).
      const order = queueRef.current;
      const ahead = order.slice(0, order.findIndex((q) => q.id === item!.id)).filter((q) => q.serverId !== undefined).length;
      try {
        await moveQueuedItem(project, convId, serverId, ahead);
      } catch (e) {
        if (gen !== queueGenRef.current) return;
        if (classifyError(e).isAuth) setAuthError(true);
        void refreshQueue(convId);
      }
    })();
    return true;
  }, [project, setLocalQueue, serverIdOf, refreshQueue]);

  const clearQueue = useCallback(() => {
    const convId = queueConvRef.current;
    // A prompt whose POST is still on its way would land after the DELETE and
    // run: cancel it the way a single remove does.
    for (const q of queueRef.current) if (q.status === "sending") cancelledPostsRef.current.add(q.id);
    setLocalQueue([]);
    if (convId === null) return;
    const gen = queueGenRef.current;
    void clearQueued(project, convId).catch(() => {
      if (gen === queueGenRef.current) void refreshQueue(convId);
    });
  }, [project, setLocalQueue, refreshQueue]);

  const resumeQueue = useCallback(() => {
    const convId = queueConvRef.current;
    if (convId === null) return;
    // A continuation that resumes the queue is already on its way; the button
    // is hidden then, and this keeps a stale click from racing it.
    if (autoContinuePendingRef.current) return;
    setPaused(null);
    const gen = queueGenRef.current;
    void (async () => {
      try {
        const res = await resumeQueueApi(project, convId);
        if (gen !== queueGenRef.current) return;
        if (res.started && res.nextItemId !== null) void followQueueStartRef.current?.(convId, res.nextItemId);
        else void refreshQueue(convId);
      } catch (e) {
        if (gen !== queueGenRef.current) return;
        if (e instanceof HttpError && e.code === "account_paused") {
          // An account-wide wall still holds: the queue stays paused, for the
          // wall's reason. No retry: the member resumes again later.
          setPaused({ reason: e.reason ?? null });
          return;
        }
        if (classifyError(e).isAuth) setAuthError(true);
        void refreshQueue(convId);
      }
    })();
  }, [project, setPaused, refreshQueue]);

  /** Whether the server still has prompts to start for this column (unpaused, not refused). */
  const queueWaiting = (): boolean => !pausedRef.current && queueRef.current.some((q) => q.status !== "failed");

  // ---- Post-turn orchestration ------------------------------------------

  const orchestrate = useCallback(
    (outcome: TurnOutcome, assistantId: string, code?: string, nextItemId?: number | null) => {
      if (!mountedRef.current) return;
      // Clear the pending latch FIRST, and let only the two branches below that
      // actually schedule something set it again.
      //
      // HIGH: this latch is what makes a Resume stand aside for a pending
      // continuation (the server drains the queue itself), so a path that
      // leaves it set with nothing scheduled strands the queue. Nothing is
      // streaming and nothing is resuming, so the composer unlocks and the Stop
      // button is gone, yet the queue can never be resumed from here. The reachable
      // case is a turn that ends WITHOUT scheduling after an auto turn set the
      // latch: a second deep stop, an auth or spend-cap end, a 403. Resetting
      // per-outcome would mean remembering to do it in each arm, which is how
      // the generic chain acquired the same hole for stall-then-credits; one
      // reset at the top cannot be forgotten.
      autoContinuePendingRef.current = false;
      setIsResuming(false);
      // Nothing is scheduled yet: the paused banner is visible again unless a
      // branch below schedules a continuation that resumes the queue.
      setQueueResuming(false);
      // A turn that did not end cleanly paused the server's queue: show it.
      if (outcome !== "natural" && outcome !== "incomplete" && queueConvRef.current !== null && queueRef.current.length > 0) {
        void refreshQueue(queueConvRef.current);
      }
      // A deep-mode budget stop: continue ONCE, automatically, in smaller steps.
      //
      // Gated on `error_final` on purpose. A user Stop classifies as "stopped"
      // and an auth/403/spend-cap end classifies as its own outcome, so none of
      // them can reach this branch whatever code rode along. The code check then
      // narrows it to the one guard that is actually resumable: the CLI session
      // survives the kill via `--resume`, so a continuation picks up the real
      // work instead of restarting it.
      //
      // Deliberately BEFORE the stall chain, and it does not touch
      // autoContinueCountRef: this continuation is not one of the two generic
      // retries and must not spend one of them.
      // No stop check here: `runTurn`'s finally clears stopRequestedRef before
      // orchestrate is reached, so reading it would always see false. What
      // actually protects a stopped turn is that Stop classifies the outcome as
      // "stopped", and the runId supersede below drops the whole call.
      const guard = guardStopContinuation(code);
      if (outcome === "error_final" && code && guard && !guardContinueUsedRef.current) {
        guardContinueUsedRef.current = true;
        autoContinuePendingRef.current = true;
        setIsResuming(true);
        setQueueResuming(chainResumesQueueRef.current);
        setMessages((prev) =>
          prev.map((m) =>
            m.id === assistantId
              ? { ...m, resuming: true, resumingLabel: guard.resumingLabel, needsContinue: false }
              : m,
          ),
        );
        if (autoContinueTimerRef.current !== null) clearTimeout(autoContinueTimerRef.current);
        autoContinueTimerRef.current = setTimeout(() => {
          autoContinueTimerRef.current = null;
          setIsResuming(false);
          // An unmount, or a manual turn that started inside the delay, wins.
          // The stop check is belt-and-braces only: Stop CLEARS this timer, so
          // a stopped turn never reaches this callback at all.
          if (!mountedRef.current || stopRequestedRef.current || isStreamingRef.current) {
            autoContinuePendingRef.current = false;
            return;
          }
          guardContinueSentCodeRef.current = code;
          void runTurnRef.current?.(guard.message, [], { auto: true });
        }, GUARD_STOP_CONTINUE_DELAY_MS);
        return;
      }
      const guardDeadEnd =
        guard !== undefined ||
        // The continuation itself never reached the daemon: 503 spawn_timeout
        // or 409 in_flight are non-retryable, so the generic rule gives no
        // button, and the guard-stop bubble that carried one was already
        // scrubbed. Without this the steering text is unreachable and the only
        // way on is to retype the whole request.
        (guardContinueSentCodeRef.current !== null && !GUARD_CONTINUE_WALL_CODES.has(code ?? ""));
      if (outcome === "error_final" && guardDeadEnd) {
        // Reached when the one automatic continuation has already been used.
        // Without this the member is left with a card and no action at all:
        // `isRetryableTurnEnd` says false for this code, so the generic
        // needsContinue never fires, and retyping the request is the only way
        // forward. The button is the same affordance a stalled turn gets once
        // its resumes run out, and `continueTurn` sends the steering message
        // rather than a bare "continue".
        setMessages((prev) =>
          prev.map((m) => (m.id === assistantId ? { ...m, needsContinue: true } : m)),
        );
        return;
      }
      if (outcome === "abnormal") {
        if (autoContinueCountRef.current < MAX_AUTO_CONTINUE) {
          const attempt = autoContinueCountRef.current + 1;
          autoContinueCountRef.current = attempt;
          autoContinuePendingRef.current = true;
          setIsResuming(true);
          setQueueResuming(chainResumesQueueRef.current);
          setMessages((prev) =>
            prev.map((m) =>
              m.id === assistantId
                ? {
                    ...m,
                    // A retryable terminal error is an intermediate state, not
                    // the final answer. Keep the error metadata so cancelling
                    // recovery can reveal the real cause again; Message hides
                    // the card only while `resuming` is true.
                    resuming: true,
                    needsContinue: false,
                  }
                : m,
            ),
          );
          if (autoContinueTimerRef.current !== null) clearTimeout(autoContinueTimerRef.current);
          const delay = AUTO_CONTINUE_BACKOFF_MS[attempt - 1] ?? AUTO_CONTINUE_BACKOFF_MS[AUTO_CONTINUE_BACKOFF_MS.length - 1];
          autoContinueTimerRef.current = setTimeout(() => {
            autoContinueTimerRef.current = null;
            setIsResuming(false);
            // A user Stop, unmount, or a manual turn that started in the gap
            // supersedes this resume.
            if (!mountedRef.current || stopRequestedRef.current || isStreamingRef.current) {
              autoContinuePendingRef.current = false;
              return;
            }
            void runTurnRef.current?.(resumeText(), [], { auto: true });
          }, delay);
        } else {
          // Auto-resumes exhausted → hand off to the manual Continue button.
          // The latch and the resuming flag are already cleared at the top.
          setMessages((prev) =>
            prev.map((m) =>
              m.id === assistantId
                ? { ...m, needsContinue: true, resuming: false, resumingLabel: undefined }
                : m,
            ),
          );
        }
      } else if (outcome === "natural") {
        // A clean completion ends any stall-chain. The SERVER drains the
        // queue: a done that handed the next prompt on names it and this
        // column follows it. Otherwise prompts still queued (and not paused)
        // are waiting on the server, for a free stream slot, and are followed
        // until something starts.
        autoContinueCountRef.current = 0;
        const convId = queueConvRef.current;
        const resumed = lastTurnResumedQueueRef.current;
        lastTurnResumedQueueRef.current = false;
        if (nextItemId != null && convId !== null) {
          // The daemon hands on only from an unpaused queue, so this done is
          // proof the pause is gone. The prompt it names follows a turn this
          // column owns.
          setPaused(null);
          void followQueueStartRef.current?.(convId, nextItemId, true);
        } else if (convId !== null && resumed) {
          // A clean resumeQueue continuation lifts the server's pause, unless a
          // Stop came after it began (then the daemon keeps it). Only the
          // server knows which: ask before showing either, and before deciding
          // between following the queue and the chime. The banner stays hidden
          // meanwhile, as it was while the continuation ran.
          setQueueResuming(true);
          const runAtEnd = runIdRef.current;
          void refreshQueue(convId).then((state) => {
            if (!mountedRef.current || runIdRef.current !== runAtEnd) return;
            setQueueResuming(false);
            if (state && !state.paused && (state.running || state.items.length > 0)) {
              void followQueueStartRef.current?.(convId, null);
            } else if (isSoundOnDone()) {
              playChime();
            }
          });
        } else {
          const waiting = queueWaiting();
          // "Sound when done": once, on the final turn. Prompts that failed to
          // save, or a paused queue this turn bypassed, do not hold it back.
          if (!waiting && isSoundOnDone()) playChime();
          if (convId !== null && waiting) {
            void followQueueStartRef.current?.(convId, null);
          } else if (convId !== null && pausedRef.current) {
            // The pause may have been lifted elsewhere (a resumeQueue turn,
            // another tab's Resume): ask, and follow if prompts are waiting.
            void refreshQueue(convId).then((state) => {
              if (state && !state.paused && (state.running || state.items.length > 0)) {
                void followQueueStartRef.current?.(convId, null);
              }
            });
          }
        }
      }
      // stopped / auth / forbidden / error_final / incomplete: the server keeps
      // the queue (paused when the turn did not end cleanly) and nothing is
      // auto-continued, but the latch above is cleared, so a Resume is not held
      // back by a continuation that will never come.
    },
    [refreshQueue, setPaused],
  );

  // ---- The streaming routine (engine) -----------------------------------
  // Reused verbatim by `send`, auto-continue and the Continue button. Assumes
  // nothing is currently streaming.
  const runTurn = async (text: string, attachments: Attachment[], opts: RunTurnOpts): Promise<void> => {
    const trimmed = text.trim();
    // An image with no caption is a real prompt (the daemon accepts one), so the
    // drop-empty guard has to look at both. Keeping it on text alone would make
    // an image-only send a silent no-op.
    if (!trimmed && attachments.length === 0) return;

    // Claim ownership of the shared streaming state for this turn. Any older
    // turn still draining now has a stale id and becomes inert.
    const myRunId = ++runIdRef.current;
    const ac = new AbortController();
    abortRef.current = ac;
    const superseded = () => runIdRef.current !== myRunId;
    // This turn owns the streaming state now, not a follow of the queue.
    followHoldsStreamRef.current = false;

    if (!opts.auto) {
      // A brand-new manual turn (typed prompt, queued prompt, or clicked
      // Continue) resets the stall chain and cancels any still-pending resume.
      autoContinueCountRef.current = 0;
      autoContinuePendingRef.current = false;
      // Re-arm the guard-stop continuation on the same signal, and deliberately
      // HERE rather than in `send`: a prompt that is merely enqueued has not
      // started yet, and re-arming at enqueue time would let the PREVIOUS
      // prompt's stop spend the waiting prompt's continuation before it ever
      // runs. One continuation per user turn, counted from where the turn
      // actually begins.
      guardContinueUsedRef.current = false;
      // Cleared only by a prompt the member actually wrote (typed or queued),
      // never by the Continue button: clicking Continue on a failed continuation
      // must still send the steering message, not a bare "continue" that walks
      // back into the panel.
      if (!opts.compact) guardContinueSentCodeRef.current = null;
      // The chain that starts here may resume the queue from its automatic
      // continuations only if the queue was not paused when it began: a prompt
      // typed past a paused queue must not silently restart it by stalling.
      chainResumesQueueRef.current = !pausedRef.current;
      if (autoContinueTimerRef.current !== null) {
        clearTimeout(autoContinueTimerRef.current);
        autoContinueTimerRef.current = null;
      }
    } else {
      // An auto-resume is now executing; keep it marked pending so a Resume
      // never races a live stall-resume chain.
      autoContinuePendingRef.current = true;
    }
    const resumesQueue = opts.auto === true && chainResumesQueueRef.current;
    lastTurnResumedQueueRef.current = resumesQueue;
    // The resume (if any) is now executing, not scheduled.
    setIsResuming(false);

    const compact = opts.compact ?? opts.auto;
    const userMsg: ChatMessage = {
      id: nextId(),
      role: "user",
      content: trimmed,
      streaming: false,
      ...(compact ? { autoContinue: true } : {}),
      // The browser already holds these bytes, so the transcript shows them
      // without waiting for the daemon to write them and hand them back.
      ...(attachments.length > 0
        ? { attachments: attachments.map((a) => ({ kind: "local" as const, attachment: a })) }
        : {}),
    };
    const assistantId = nextId();
    const assistantMsg: ChatMessage = {
      id: assistantId,
      role: "assistant",
      content: "",
      streaming: true,
      startedAt: Date.now(),
    };

    // A new turn begins: nothing is "resuming" any more, and a manual/continue
    // action clears a stale Continue button.
    setMessages((prev) => {
      // A retryable failure before any text produced an empty assistant bubble
      // that existed only to show "resuming" during the backoff. Drop that
      // placeholder when the automatic retry actually starts, otherwise it
      // turns into a permanent "(no response)" bubble. Preserve partial
      // answers: they are useful context and must remain in the transcript.
      const withoutEmptyRecovery = opts.auto
        ? prev.filter((m) => !(
            m.role === "assistant" &&
            m.resuming &&
            !m.content &&
            (m.progress?.length ?? 0) === 0 &&
            (m.attachments?.length ?? 0) === 0
          ))
        : prev;
      const scrubbed = withoutEmptyRecovery.map((m) => {
        let nm = m;
        if (m.resuming) {
          nm = {
            ...nm,
            resuming: false,
            resumingLabel: undefined,
            // Once the retry begins, retained partial/progress turns are
            // historical context. Their transient terminal error is no longer
            // actionable and must not reappear beside a successful retry.
            ...(opts.auto ? { error: undefined, errorInfo: undefined } : {}),
          };
        }
        if (!opts.auto && m.needsContinue) nm = { ...nm, needsContinue: false };
        return nm;
      });
      return [...scrubbed, userMsg, assistantMsg];
    });
    setIsStreaming(true);
    isStreamingRef.current = true;
    setError(null);
    setErrorInfo(null);
    stopRequestedRef.current = false;
    watchedTurnIdRef.current = null;

    const finalizeAssistant = (patch: Partial<ChatMessage>) => {
      setMessages((prev) =>
        prev.map((m) => (m.id === assistantId ? { ...m, ...patch, streaming: false } : m)),
      );
    };

    const boundConvId = currentConversationId ?? conversationId ?? undefined;
    // Fire once on the first received frame so the sidebar shows the new
    // conversation's name immediately (the user turn is persisted server-side
    // before any frame is sent), then again in `finally` for the final meta.
    let activityNotified = false;
    let outcome: TurnOutcome = "incomplete";
    // The machine code this turn ended on, if any. Post-turn orchestration needs
    // it to tell a deep-mode budget stop from every other non-retryable end;
    // `outcome` alone collapses them all into "error_final".
    let endCode: string | undefined;
    let queueNextItem: number | null = null;

    // The shared reducer's per-turn hooks. Only the terminal frames are
    // caller-specific: how this turn ENDED drives outcome tracking, and through
    // it the auto-continue chain and the queue — none of which a reattached
    // turn owns. The token/progress/snapshot mutations live in applyFrame.
    const frameCtx: FrameCtx = {
      assistantId,
      superseded,
      setMessages,
      onDone: (subtype, next) => {
        watchedTurnIdRef.current = null;
        if (stopRequestedRef.current) {
          finalizeAssistant({ interrupted: true });
          outcome = "stopped";
        } else if (subtype === "interrupted") {
          // Subprocess exited mid-turn (not a user Stop): resume it.
          finalizeAssistant({});
          outcome = "abnormal";
        } else {
          finalizeAssistant({});
          outcome = "natural";
          queueNextItem = next ?? null;
        }
      },
      onError: (error, code) => {
        watchedTurnIdRef.current = null;
        endCode = code;
        if (AUTH_ERROR_MARKERS.has(error)) {
          setAuthError(true);
          finalizeAssistant(stopRequestedRef.current ? { interrupted: true } : { error });
          outcome = stopRequestedRef.current ? "stopped" : "auth";
        } else if (!stopRequestedRef.current) {
          // SSE error carries a code + text (no HttpError), so build the
          // FriendlyError directly rather than via toFriendly().
          finalizeAssistant({ errorInfo: friendlyError({ code, rawMessage: error }) });
          outcome = isRetryableTurnEnd(code, error) ? "abnormal" : "error_final";
        } else {
          finalizeAssistant({ interrupted: true });
          outcome = "stopped";
        }
      },
      onSnapshot: (ev) => {
        if (typeof ev.turnId === "number") watchedTurnIdRef.current = ev.turnId;
        // A column that sent without a conversation learns it here, so a
        // prompt queued behind this turn goes to the right queue.
        if (typeof ev.conversationId === "number" && queueConvRef.current === null) {
          queueConvRef.current = ev.conversationId;
          setCurrentConversationId(ev.conversationId);
          settleConvWaiters(ev.conversationId);
        }
      },
    };

    try {
      for await (const ev of sendChat({
        project,
        message: trimmed,
        conversationId: boundConvId !== null ? boundConvId : undefined,
        // Undefined, not [], when there is nothing to send: the key then drops
        // out of the request body and a text-only turn is unchanged on the wire.
        images: attachments.length > 0 ? attachments.map(toOutgoing) : undefined,
        ...(resumesQueue ? { resumeQueue: true } : {}),
      }, ac.signal) as AsyncIterable<StreamEvent>) {
        if (!activityNotified) {
          activityNotified = true;
          notifyConversationActivity(project);
        }
        applyFrame(ev, frameCtx);
      }
      // Stream ended without a terminal frame (session killed via /stop, or the
      // connection dropped mid-turn).
      if (outcome === "incomplete") {
        if (stopRequestedRef.current) {
          finalizeAssistant({ interrupted: true });
          outcome = "stopped";
        } else {
          finalizeAssistant({});
          outcome = "abnormal";
        }
      }
    } catch (e) {
      if (isAbort(e) || ac.signal.aborted) {
        // Intentional supersede (Stop / new turn / unmount / conversation
        // change): no error bubble, no orchestrate. The runId guard below also
        // covers it; the optimistic Stop path already finalized the bubble.
        outcome = "stopped";
      } else {
        const { isAuth, err } = classifyError(e);
        if (isAuth) {
          setAuthError(true);
          finalizeAssistant(stopRequestedRef.current ? { interrupted: true } : { error: err.message });
          outcome = stopRequestedRef.current ? "stopped" : "auth";
        } else if (err instanceof HttpError && err.status === 403) {
          // Preserve the forbidden-view flow (ChatColumn swaps the whole column
          // for ForbiddenView when this error surfaces).
          setError(err);
          setErrorInfo(toFriendly(err));
          finalizeAssistant(stopRequestedRef.current ? { interrupted: true } : { error: err.message });
          outcome = stopRequestedRef.current ? "stopped" : "forbidden";
        } else if (!stopRequestedRef.current) {
          finalizeAssistant({ errorInfo: toFriendly(err) });
          const code = err instanceof HttpError ? err.code : undefined;
          endCode = code;
          outcome = isRetryableTurnEnd(code, err.message) ? "abnormal" : "error_final";
        } else {
          finalizeAssistant({ interrupted: true });
          outcome = "stopped";
        }
      }
    } finally {
      // Only the turn that still owns the streaming state may reset it. A
      // superseded turn keeps its hands off the newer turn's shared state.
      if (!superseded()) {
        setIsStreaming(false);
        isStreamingRef.current = false;
        stopRequestedRef.current = false;
        if (abortRef.current === ac) abortRef.current = null;
        // Resync the sidebar with the final turn count + timestamp (and the
        // first-message preview, if the first-frame notify never fired because
        // the request failed before streaming).
        notifyConversationActivity(project);
        // The transcript changed; drop the cached turns so the next open of
        // this conversation revalidates and shows the new exchange.
        invalidateTurns(project, boundConvId !== null ? boundConvId : undefined);
        // A prompt queued while this turn ran without a conversation, whose
        // snapshot never came (the POST failed first): let it create one.
        settleConvWaiters(queueConvRef.current);
      }
    }

    // Post-turn: auto-continue on an abnormal stall, or follow the server's
    // queue on a natural completion. Skipped entirely for a superseded turn.
    if (!superseded()) {
      orchestrate(outcome, assistantId, endCode, queueNextItem as number | null);
    }
  };
  // Keep the latest engine reachable from timers, the queue, and buttons.
  runTurnRef.current = runTurn;

  // ---- Public actions ----------------------------------------------------

  const send = useCallback(
    async (text: string, attachments: Attachment[] = []): Promise<void> => {
      const trimmed = text.trim();
      if (!trimmed && attachments.length === 0) return;
      // A draft carried over from a queued prompt may hold stored images by
      // reference only (empty base64). Load their bytes before anything is
      // sent, so neither /chat nor the queue ever receives an empty image. Only
      // then is there an await: a plain send stays synchronous up to the enqueue.
      let ready = attachments;
      if (attachments.some((a) => a.storedId !== undefined && a.base64 === "")) {
        try {
          ready = await rehydrateStoredAttachments(attachments);
        } catch (e) {
          if (!mountedRef.current) return;
          const { isAuth, err } = classifyError(e);
          if (isAuth) setAuthError(true);
          else { setError(err); setErrorInfo(toFriendly(err)); }
          return;
        }
        if (!mountedRef.current) return;
      }
      // FIFO: while a turn streams, or while the queue has prompts and is NOT
      // paused, a new prompt joins the back of the server's queue. A paused
      // queue waits for Resume; a prompt typed meanwhile runs now and does not
      // resume it. Prompts the server refused are not waiting for anything.
      if (isStreamingRef.current || (queueRef.current.some((q) => q.status !== "failed") && !pausedRef.current)) {
        enqueue(trimmed, ready);
        return;
      }
      await runTurnRef.current?.(trimmed, ready, { auto: false });
    },
    [enqueue, toFriendly],
  );

  const continueTurn = useCallback(() => {
    if (isStreamingRef.current) return;
    // A manual continue supersedes any pending stall-resume and resets the chain
    // so a fresh stall gets its full budget of auto-resumes again.
    if (autoContinueTimerRef.current !== null) {
      clearTimeout(autoContinueTimerRef.current);
      autoContinueTimerRef.current = null;
    }
    autoContinuePendingRef.current = false;
    autoContinueCountRef.current = 0;
    setIsResuming(false);
    // A guard stop needs the STEERING message, not a bare "continue": the panel
    // is what hit the guard, so an unsteered resume is likely to hit it again.
    // Keyed off how the last assistant turn actually ended, because this button
    // is shared with the stall path, where "continue" is the right word, and
    // because which steering message is right depends on WHICH guard fired.
    const lastAssistant = [...messagesRef.current].reverse().find((m) => m.role === "assistant");
    // Either the turn plainly ended on a guard, or it WAS the continuation and
    // died before reaching the daemon. In the second case the failed turn's own
    // code names the transport, not the guard, so only the ref remembers.
    const endedOn = lastAssistant?.errorInfo?.code;
    const fromCard = guardStopContinuation(endedOn);
    // Record which guard this click is steering away from, exactly as the
    // automatic timer does. The click runs `compact`, which SKIPS the reset in
    // runTurn, so without this the marker keeps whatever the last automatic
    // continuation left behind. In a mixed chain that is the wrong guard: a
    // budget stop continues (marker `deep_mode_limit`), the continuation ends
    // on a write block, the click correctly sends the write-block text, that
    // turn dies on a transport code, and the NEXT click falls through to
    // `resumeText()` and resends the BUDGET text into a write-block problem.
    if (fromCard && endedOn) guardContinueSentCodeRef.current = endedOn;
    const text = fromCard?.message ?? resumeText();
    void runTurnRef.current?.(text, [], { auto: false, compact: true });
  }, []);

  const stop = useCallback(async () => {
    const wasStreaming = isStreaming;
    const wasResuming = autoContinuePendingRef.current;
    // Also usable during the auto-resume backoff window (nothing is streaming
    // yet, but a resume is scheduled).
    if (!wasStreaming && !wasResuming) return;

    // Supersede + abort the in-flight turn so it stops draining and can never
    // reclassify its terminal frame as "abnormal" or schedule a resume. This is
    // the ONE abort that survives: Stop is a deliberate user action, and
    // `POST /chat/:project/stop` below is what actually kills the session.
    // A reattached turn is stopped the same way — the daemon owns it either way.
    runIdRef.current++;
    abortRef.current?.abort();
    reattachAbortRef.current?.abort();
    queueWaitAbortRef.current?.abort();
    stopRequestedRef.current = true;

    // Cancel any scheduled/pending resume and clear the chain.
    if (autoContinueTimerRef.current !== null) {
      clearTimeout(autoContinueTimerRef.current);
      autoContinueTimerRef.current = null;
    }
    autoContinuePendingRef.current = false;
    autoContinueCountRef.current = 0;
    setIsResuming(false);
    setQueueResuming(false);

    // Optimistic: flip the streaming message to interrupted AND unlock the
    // composer immediately. The aborted SSE stream tears down in the background.
    setMessages((prev) =>
      prev.map((m) =>
        m.role === "assistant" && m.streaming
          ? { ...m, interrupted: true, streaming: false }
          : m.role === "assistant" && m.resuming
            ? {
                ...m,
                resuming: false,
                resumingLabel: undefined,
                // A guard stop cancelled mid-delay keeps its card AND gains the
                // button, so stopping the continuation is not the same as
                // losing every way to carry on.
                ...(guardStopContinuation(m.errorInfo?.code) ? { needsContinue: true } : {}),
                // An interrupted turn has no error card to restore. Mark it so
                // the existing Retry action remains available after Stop.
                ...(!m.error && !m.errorInfo ? { interrupted: true } : {}),
              }
            : m,
      ),
    );
    setIsStreaming(false);
    isStreamingRef.current = false;
    followHoldsStreamRef.current = false;
    // The daemon pauses a queue that has prompts for every Stop (even one that
    // names a turn already over). Show it now: a prompt typed before the
    // answer lands must see a paused queue, and run rather than queue.
    if (queueConvRef.current !== null && queueRef.current.some((q) => q.status !== "failed")) {
      setPaused({ reason: "stopped" });
    }

    try {
      // Only ask the daemon to kill the subprocess if a turn was actually
      // streaming; a pure backoff-window Stop has nothing live to kill.
      if (wasStreaming) {
        const convId = currentConversationId ?? conversationId ?? undefined;
        // The daemon refuses a turn id that is not an integer (400).
        const turnId = watchedTurnIdRef.current;
        await stopChat(project, convId !== null ? convId : undefined, Number.isInteger(turnId) ? turnId! : undefined);
      }
    } catch (e) {
      const { isAuth, err } = classifyError(e);
      if (isAuth) setAuthError(true);
      else { setError(err); setErrorInfo(toFriendly(err)); }
    } finally {
      // Reset at the END so a later manual send is not blocked.
      stopRequestedRef.current = false;
      // The daemon paused the queue for this Stop: show it.
      const cid = queueConvRef.current;
      if (cid !== null) void refreshQueue(cid);
    }
  }, [project, conversationId, currentConversationId, isStreaming, toFriendly, refreshQueue]);

  const loadLatestHistory = useCallback(async () => {
    setIsLoadingHistory(true);
    setError(null);
    setErrorInfo(null);
    try {
      resetQueueLocal();
      const convs = await listConversations(project);
      if (convs.length === 0) {
        setMessages([]);
        queueConvRef.current = null;
        setCurrentConversationId(null);
        return;
      }
      // The list comes back in the user's sidebar order, so "latest" has to
      // be picked by creation time, not by position.
      const latest = convs.reduce((a, b) => (b.created_at > a.created_at ? b : a));
      const turns = await fetchTurns(project, latest.id);
      setMessages(turnsToMessages(turns));
      setCurrentConversationId(latest.id);
      queueConvRef.current = latest.id;
      void refreshQueue(latest.id);
    } catch (e) {
      const { isAuth, err } = classifyError(e);
      if (isAuth) setAuthError(true);
      else { setError(err); setErrorInfo(toFriendly(err)); }
      // Do NOT clear messages on failure — fall back to whatever was there
      // so the user can keep sending in a fresh in-memory transcript.
    } finally {
      setIsLoadingHistory(false);
    }
  }, [project, resetQueueLocal, refreshQueue]);

  /**
   * A reattach that produced NO frames at all. That is not "the turn finished":
   * three different situations collapse to silence — nothing is in flight, the
   * route answered with a content type this client does not stream, or the body
   * was empty — and they are indistinguishable from here. Inferring completion
   * from silence would paint a live turn as finished, which is the exact bug
   * this whole branch exists to remove.
   *
   * So it asks instead of guessing, from the two places that actually know:
   * the turns (the row's own final status) and the conversation summary
   * (`streaming`, which the daemon computes from its live in-flight registry,
   * not from the row). They disagree only when a row is orphaned — its
   * subprocess died without settling it, and the daemon has not rebooted into
   * its boot reconciliation yet. The registry wins there: an eternal spinner
   * over a turn nothing is writing is a worse lie than "interrupted", and
   * interrupted is exactly what that reboot would have written.
   *
   * A summary with no `streaming` field (a daemon older than server-owned
   * turns) is not evidence of anything and never overrides the row.
   */
  const reconcileQuietReattach = useCallback(
    async (convId: number, superseded: () => boolean): Promise<void> => {
      const [fresh, summaries] = await Promise.all([
        fetchTurns(project, convId),
        // The summary is corroboration, not the answer; losing it must not cost
        // us the refetch, which is the part that matters.
        listConversations(project).catch(() => null),
      ]);
      if (superseded() || !mountedRef.current) return;
      setMessages(turnsToMessages(fresh));
      const last = fresh.at(-1);
      if (!last || last.role !== "assistant" || last.status !== "streaming") return;
      if (summaries?.find((c) => c.id === convId)?.streaming === false) {
        setMessages((prev) =>
          prev.map((m) => (m.id === `t-${last.id}` ? { ...m, streaming: false, interrupted: true } : m)),
        );
      }
    },
    [project],
  );

  /**
   * Resume rendering a turn the daemon is already running.
   *
   * Guarded by the same generation counter as `runTurn`, so a fast switch away
   * and back cannot leave two readers patching one message, and it runs the same
   * `applyFrame` reducer, so a turn watched from here renders identically to one
   * this column started.
   *
   * What it deliberately does NOT do is orchestrate. `onDone`/`onError` finalize
   * the bubble and stop there: the auto-continue chain, the queue drain, and the
   * chime all belong to a turn this column OWNS. Auto-resuming a turn the user
   * merely opened would send a `continue` — and spend — on work they did not
   * start, from whichever tab happened to be watching.
   */
  const driveReattach = useCallback(
    async (convId: number, assistantId: string, opts: { owned?: boolean } = {}): Promise<void> => {
      const owned = opts.owned === true;
      // One reader per column. Superseding an earlier drive makes it inert but
      // leaves the daemon holding its socket, so hang up on it as well.
      reattachAbortRef.current?.abort();
      const ac = new AbortController();
      reattachAbortRef.current = ac;
      const myRunId = ++runIdRef.current;
      const superseded = () => runIdRef.current !== myRunId;
      // The reader owns the streaming state now, not a follow of the queue.
      followHoldsStreamRef.current = false;
      if (owned) {
        // A queue turn this column owns begins a NEW user turn exactly like a
        // typed prompt does in runTurn: re-arm the one guard-stop continuation
        // and the stall chain for it, or the second queued prompt that
        // deep-stops would find the first prompt's latch spent. Its automatic
        // continuations resume the queue (the server started it unpaused).
        autoContinueCountRef.current = 0;
        guardContinueUsedRef.current = false;
        guardContinueSentCodeRef.current = null;
        chainResumesQueueRef.current = true;
        lastTurnResumedQueueRef.current = false;
      }

      setIsStreaming(true);
      isStreamingRef.current = true;

      const finalize = (patch: Partial<ChatMessage>) => {
        setMessages((prev) =>
          prev.map((m) => (m.id === assistantId ? { ...m, ...patch, streaming: false } : m)),
        );
      };

      // A subscriber's very first frame is ALWAYS a snapshot — `ActiveTurn.subscribe`
      // enqueues one unconditionally before it registers the subscriber
      // (src/active-turn.ts) — so this flag separates the two things that both
      // arrive as `{type:"error"}`: the TURN failing, which the bubble must
      // show, and this client failing to READ it, which the turn knows nothing
      // about. `reattachChat` reports a dead fetch as an error frame rather than
      // a throw, and without the invariant the two are one string apart.
      let sawSnapshot = false;
      // The reconcile runs at most once per drive. It is reachable from two
      // places — a stream that ended quietly, and one that threw — and running
      // it twice would mean a second refetch for a question already asked.
      let reconciled = false;
      const reconcileOnce = async (): Promise<void> => {
        if (reconciled) return;
        reconciled = true;
        try {
          await reconcileQuietReattach(convId, superseded);
        } catch (e) {
          // The refetch failed too, so we still do not know what the turn is
          // doing and say nothing about it. A session expiry is the exception:
          // it has to surface wherever it happens.
          if (!superseded() && mountedRef.current && classifyError(e).isAuth) setAuthError(true);
        }
      };

      // `owned`: a turn the queue started for THIS column's prompt. It is
      // orchestrated like a turn this column POSTed (auto-continue, guard
      // continuation, chime), which the queue used to get for free when the
      // client ran the prompts itself. A reattach to a turn merely opened is
      // not: that would spend on work the viewer did not start.
      let outcome: TurnOutcome = "incomplete";
      let endCode: string | undefined;
      let nextItem: number | null = null;

      try {
        for await (const ev of reattachChat(project, convId, ac.signal)) {
          if (superseded() || !mountedRef.current) return;
          if (ev.type === "snapshot") sawSnapshot = true;
          else if (!sawSnapshot) continue; // transport failure, not the turn's
          applyFrame(ev, {
            assistantId,
            superseded,
            setMessages,
            onSnapshot: (snap) => {
              if (typeof snap.turnId === "number") watchedTurnIdRef.current = snap.turnId;
            },
            onDone: (subtype, next) => {
              watchedTurnIdRef.current = null;
              // `interrupted` means the subprocess died mid-turn, and the daemon
              // broadcasts it to EVERY subscriber (src/active-turn.ts:430), so a
              // reattached client sees exactly the truncation the owning column
              // would have. It must not render as a normal completion: the user
              // cannot tell a cut-off answer from a finished one by reading it.
              //
              // A column that merely opened the conversation will not spend on
              // the user's behalf. Offering the button says what happened AND
              // hands them the decision. A queue turn this column owns is
              // auto-resumed by orchestrate instead, like a turn it POSTed.
              if (subtype === "interrupted") {
                finalize(owned ? {} : { needsContinue: true });
                outcome = "abnormal";
                return;
              }
              finalize({});
              outcome = "natural";
              nextItem = next ?? null;
            },
            onError: (error, code) => {
              watchedTurnIdRef.current = null;
              endCode = code;
              if (AUTH_ERROR_MARKERS.has(error)) {
                setAuthError(true);
                finalize({ error });
                outcome = "auth";
                return;
              }
              if (owned) {
                finalize({ errorInfo: friendlyError({ code, rawMessage: error }) });
                outcome = isRetryableTurnEnd(code, error) ? "abnormal" : "error_final";
                return;
              }
              // Same split the send path makes, by the same classifier: a
              // retryable end gets a Continue button, and `credits_exhausted`,
              // the spend caps, and the deliberate stops do not. A guard stop
              // is not retryable and never auto-continues for a watcher, but it
              // is resumable by a deliberate click, and this path is the one a
              // reload lands on, so without the button a reloaded guard stop
              // has no way forward at all.
              finalize({
                errorInfo: friendlyError({ code, rawMessage: error }),
                ...(isRetryableTurnEnd(code, error) || guardStopContinuation(code)
                  ? { needsContinue: true }
                  : {}),
              });
              outcome = "error_final";
            },
          });
        }
        // Nothing that came from the turn itself. Silence and an unreadable
        // stream are the same amount of evidence about whether it is still
        // running, which is none, so both go and ask.
        if (!sawSnapshot) await reconcileOnce();
      } catch (e) {
        if (isAbort(e) || ac.signal.aborted) return;
        if (superseded() || !mountedRef.current) return;
        // A failed reattach is not a failed turn — the transcript already holds
        // the checkpointed text and the row keeps its own status — but it is
        // still a NON-ANSWER, and the same one. `reattachChat` throws an
        // HttpError for every non-200 except 401/403 (a proxy 5xx, say), and a
        // mid-stream reader rejection lands here too. Without this the message
        // keeps `streaming: true` while `isStreaming` goes false: a spinner with
        // no Stop button that never self-corrects until the column is reopened.
        if (classifyError(e).isAuth) setAuthError(true);
        if (!sawSnapshot) await reconcileOnce();
      } finally {
        if (!superseded()) {
          setIsStreaming(false);
          isStreamingRef.current = false;
          if (reattachAbortRef.current === ac) reattachAbortRef.current = null;
          if (sawSnapshot) {
            // The turn ended (or moved on) while this column watched: resync the
            // sidebar and drop the cached turns so the next open sees the final
            // row rather than the streaming one it was opened on. Skipped when
            // nothing streamed, because the quiet path just refetched.
            notifyConversationActivity(project);
            invalidateTurns(project, convId);
          }
        }
      }
      if (superseded() || !mountedRef.current) return;
      const ended = outcome as TurnOutcome;
      const handedOn = nextItem as number | null;
      if (owned && ended !== "incomplete") {
        orchestrate(ended, assistantId, endCode, handedOn);
      } else if (handedOn !== null) {
        // Handed on by a turn this column only watched: the next one is its
        // own only if this column enqueued that prompt.
        void followQueueStartRef.current?.(convId, handedOn, false);
      } else if (ended === "natural" && queueWaiting()) {
        // A watched turn ended cleanly without handing anything on while
        // prompts wait (no free slot yet): follow them until one starts.
        void followQueueStartRef.current?.(convId, null);
      }
    },
    [project, reconcileQuietReattach, orchestrate],
  );

  /**
   * Open a reader for a loaded transcript whose last turn is still being
   * written. `turns` is whatever the caller most recently got from the server —
   * never the cached copy, which is what said "streaming" in the first place.
   */
  const reattachIfLive = useCallback(
    (convId: number, turns: TurnRow[], owned = false): void => {
      const last = turns.at(-1);
      if (last && last.role === "assistant" && last.status === "streaming") {
        void driveReattach(convId, `t-${last.id}`, { owned });
      }
    },
    [driveReattach],
  );

  /**
   * The server started, or is about to start, a queued prompt. Wait for it on
   * GET .../queue/next, merge the transcript, and reattach to it. `nextItemId`
   * is the reserved item (from a done frame, an enqueue or a Resume); null
   * means nothing was handed on yet but prompts are waiting. It keeps
   * following while the server is starting something (`running`) or prompts
   * remain, and stops at a pause, an empty idle queue, or when the column
   * moves on.
   *
   * The started turn is reattached as this column's OWN (auto-continue, guard
   * continuation, chime) only when the column enqueued that prompt, or when
   * it is `nextItemId` handed on by a turn this column owned
   * (`predecessorOwned`). Any other column watching the conversation gets the
   * Continue buttons instead, so two tabs never both spend on one turn.
   */
  const followQueueStart = useCallback(async (
    convId: number,
    nextItemId: number | null,
    predecessorOwned = false,
    // A start is known to be in flight (GET queue said `running`) even though
    // its item id is not: keep the column busy through it, like a known item.
    holdStream = false,
  ): Promise<void> => {
    if (queueConvRef.current !== convId || !mountedRef.current) return;
    // The turn this column watched is over; until the next one starts, Stop
    // names no turn rather than the finished one.
    watchedTurnIdRef.current = null;
    if (nextItemId !== null) dropHandedOn(nextItemId);
    queueWaitAbortRef.current?.abort();
    const ac = new AbortController();
    queueWaitAbortRef.current = ac;
    const runAtStart = runIdRef.current;
    const gen = queueGenRef.current;
    const stale = (): boolean =>
      ac.signal.aborted || runIdRef.current !== runAtStart || gen !== queueGenRef.current || !mountedRef.current;
    const isOwn = (itemId: number): boolean =>
      ownServerIdsRef.current.has(itemId) || (predecessorOwned && itemId === nextItemId);
    // A known start keeps the column streaming through the spawn, so a prompt
    // typed in that gap is queued instead of refused as in flight, and Stop
    // reaches the reservation. A follow this one superseded may have set it:
    // then this one holds it, and releases it.
    let known = nextItemId !== null || holdStream || followHoldsStreamRef.current;
    let waitFor = nextItemId;
    if (known) {
      setIsStreaming(true);
      isStreamingRef.current = true;
      followHoldsStreamRef.current = true;
    }
    const release = (): void => {
      if (known && !stale()) {
        setIsStreaming(false);
        isStreamingRef.current = false;
        followHoldsStreamRef.current = false;
      }
      known = false;
    };
    try {
      for (;;) {
        let res: NextStart;
        try {
          res = await awaitQueueStart(project, convId, QUEUE_WAIT_MS, waitFor ?? undefined, ac.signal);
        } catch (e) {
          if (isAbort(e) || stale()) return;
          // Too many of this member's tabs are waiting: back off and ask again
          // rather than give up on the queue.
          if (e instanceof HttpError && e.code === "too_many_waiters") {
            await sleep(QUEUE_WAITERS_BACKOFF_MS, ac.signal);
            if (stale()) return;
            continue;
          }
          throw e;
        }
        if (stale()) return;
        if (res.started) {
          watchedTurnIdRef.current = res.turnId;
          dropHandedOn(res.itemId);
          const fresh = await fetchTurns(project, convId);
          if (stale()) return;
          setMessages((prev) => mergeTurnsIntoMessages(prev, fresh));
          const last = fresh.at(-1);
          if (last && last.role === "assistant" && last.status === "streaming") {
            void refreshQueue(convId);
            reattachIfLive(convId, fresh, isOwn(res.itemId));
            return;
          }
          // It started and already ended: nothing to watch; see what is left.
          watchedTurnIdRef.current = null;
          waitFor = null;
        } else {
          // The wait ran out. The item it named may have started, finished and
          // handed on between two waits, and a wait naming it would never be
          // answered again. Without an item the daemon answers for a
          // reservation still starting and for the queue turn that is live.
          waitFor = null;
        }
        const state = await refreshQueue(convId);
        if (stale()) return;
        if (!state || state.paused || (!state.running && state.items.length === 0)) {
          release();
          return;
        }
        // Prompts wait for a free slot and nothing is starting yet: unlock the
        // composer meanwhile. While a reservation is in flight it stays busy.
        if (!state.running) release();
      }
    } catch (e) {
      if (isAbort(e) || stale()) return;
      if (classifyError(e).isAuth) setAuthError(true);
      release();
    } finally {
      if (queueWaitAbortRef.current === ac) queueWaitAbortRef.current = null;
    }
  }, [project, refreshQueue, reattachIfLive, dropHandedOn]);
  followQueueStartRef.current = followQueueStart;

  /**
   * Reload the transcript after the server ran a prompt this column did not
   * watch start. `ownUserTurnId` is the user turn that prompt became: a live
   * last turn answering THAT turn is this column's own; any other live turn
   * (another tab's prompt) is only watched.
   */
  const reloadTurns = useCallback(async (convId: number, ownUserTurnId?: number): Promise<void> => {
    invalidateTurns(project, convId);
    // A live turn or a follow on screen reloads the transcript when it ends.
    if (isStreamingRef.current || queueWaitAbortRef.current !== null) return;
    const gen = queueGenRef.current;
    try {
      const fresh = await fetchTurns(project, convId);
      if (gen !== queueGenRef.current || queueConvRef.current !== convId || !mountedRef.current) return;
      if (isStreamingRef.current) return;
      setMessages((prev) => mergeTurnsIntoMessages(prev, fresh));
      const asked = fresh.at(-2);
      const owned = ownUserTurnId !== undefined && asked?.role === "user" && asked.id === ownUserTurnId;
      reattachIfLive(convId, fresh, owned);
    } catch (e) {
      if (gen === queueGenRef.current && mountedRef.current && classifyError(e).isAuth) setAuthError(true);
    }
  }, [project, reattachIfLive]);
  reloadTurnsRef.current = reloadTurns;

  const loadConversation = useCallback(
    async (conversationId: number) => {
      setError(null);
      setErrorInfo(null);
      // The switch effect bumps `runIdRef` on every conversation change, so it
      // doubles as this load's generation: a load that resolves after the column
      // moved on must not open a reader onto a conversation nobody is looking at.
      const loadRunId = runIdRef.current;
      const staleLoad = () => runIdRef.current !== loadRunId;
      queueConvRef.current = conversationId;
      const queueRead = refreshQueue(conversationId);
      /**
       * Coming back between two prompts: the next one may be starting or
       * waiting for a slot, and nothing else would ever follow it (the column
       * only reattaches a turn that is already streaming), so the column would
       * sit frozen and a typed prompt would be refused as in flight. When
       * nothing was reattached and the queue can move, follow it.
       */
      const followIfQueued = async (turns: TurnRow[]): Promise<void> => {
        const last = turns.at(-1);
        if (last && last.role === "assistant" && last.status === "streaming") return;
        const state = await queueRead;
        if (!mountedRef.current || staleLoad() || queueConvRef.current !== conversationId) return;
        if (!state || state.paused || (!state.running && state.items.length === 0)) return;
        if (queueWaitAbortRef.current !== null || isStreamingRef.current) return;
        // Running: a start is in flight, so the column stays busy (a typed
        // prompt queues instead of being refused as in flight).
        void followQueueStartRef.current?.(conversationId, null, false, state.running);
      };

      // Cache hit: render the known transcript immediately (no "Loading
      // history…" flash) and revalidate in the background. This is the common
      // case when switching between, or back to, conversations.
      const cached = getCachedTurns(project, conversationId);
      if (cached) {
        setMessages(turnsToMessages(cached));
        setCurrentConversationId(conversationId);
        setIsLoadingHistory(false);
        try {
          const fresh = await fetchTurns(project, conversationId);
          if (mountedRef.current) setMessages(turnsToMessages(fresh));
          // Off `fresh`, not `cached`: the cached copy is a snapshot of the
          // moment the user left, so it can claim a turn is live that finished
          // in between, and can miss one that started since.
          if (mountedRef.current && !staleLoad()) {
            reattachIfLive(conversationId, fresh);
            void followIfQueued(fresh);
          }
        } catch (e) {
          // We already showed cached content, so only surface a real auth
          // failure (session expired); swallow transient network/5xx errors
          // rather than flashing a banner over a populated transcript.
          if (mountedRef.current && classifyError(e).isAuth) setAuthError(true);
        }
        return;
      }

      // Cache miss: show the loading state for the round-trip, then cache it.
      setIsLoadingHistory(true);
      try {
        const turns = await fetchTurns(project, conversationId);
        setMessages(turnsToMessages(turns));
        setCurrentConversationId(conversationId);
        if (mountedRef.current && !staleLoad()) {
          reattachIfLive(conversationId, turns);
          void followIfQueued(turns);
        }
      } catch (e) {
        const { isAuth, err } = classifyError(e);
        if (isAuth) setAuthError(true);
        else { setError(err); setErrorInfo(toFriendly(err)); }
      } finally {
        setIsLoadingHistory(false);
      }
    },
    [project, reattachIfLive, refreshQueue],
  );

  const startNew = useCallback(async () => {
    setError(null);
    setErrorInfo(null);
    try {
      const row = await createConversation(project);
      resetQueueLocal();
      queueConvRef.current = row.id;
      setMessages([]);
      setCurrentConversationId(row.id);
    } catch (e) {
      const { isAuth, err } = classifyError(e);
      if (isAuth) setAuthError(true);
      else { setError(err); setErrorInfo(toFriendly(err)); }
    }
  }, [project, resetQueueLocal]);

  // When the bound conversationId prop changes, load its history AND tear down
  // any orchestration left over from the previous conversation — supersede the
  // in-flight turn, cancel a scheduled resume, reset the chain, and forget the
  // local queue mirror, so nothing leaks across a conversation switch. The turn itself is
  // deliberately NOT aborted: it keeps running on the daemon and is waiting to
  // be reattached to when the user comes back. Only the reattach reader hangs
  // up, because a reader the user has left is pure cost. Safe on the initial
  // mount (both controller refs start null).
  useEffect(() => {
    runIdRef.current++;
    reattachAbortRef.current?.abort();
    if (autoContinueTimerRef.current !== null) {
      clearTimeout(autoContinueTimerRef.current);
      autoContinueTimerRef.current = null;
    }
    autoContinuePendingRef.current = false;
    autoContinueCountRef.current = 0;
    // Re-arm with the rest of the orchestration: the new conversation's first
    // guard stop is its own, and must not inherit a latch from the one the user
    // just left.
    guardContinueUsedRef.current = false;
    // The marker goes with it. Left set, it leaks the OLD conversation's guard
    // into the new one twice over: `guardDeadEnd` offers a Continue button for
    // a transport code in a conversation that never had a guard stop, and the
    // click then sends the old conversation's steering message here.
    guardContinueSentCodeRef.current = null;
    isStreamingRef.current = false;
    stopRequestedRef.current = false;
    setIsResuming(false);
    setIsStreaming(false);
    // LOCAL reset only: the queue belongs to the conversation on the server
    // and keeps draining there. The next conversation's queue loads with it.
    resetQueueLocal();
    queueConvRef.current = conversationId ?? null;
    watchedTurnIdRef.current = null;

    if (conversationId == null) {
      setMessages([]);
      setCurrentConversationId(null);
      return;
    }
    void loadConversation(conversationId);
    // loadConversation/resetQueueLocal are stable; excluding them avoids a double-load
    // on identity change.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [conversationId]);

  const canContinue = !isStreaming && messages.some((m) => m.needsContinue);

  return {
    messages,
    send,
    stop,
    isStreaming,
    isLoadingHistory,
    authError,
    clearAuthError,
    error,
    errorInfo,
    clearError,
    loadLatestHistory,
    loadConversation,
    startNew,
    currentConversationId,
    continueTurn,
    canContinue,
    isResuming,
    queue,
    removeQueued,
    updateQueued,
    moveQueued,
    clearQueue,
    // Hidden while a continuation that resumes the queue is pending or running.
    queuePaused: queuePause !== null && !queueResuming,
    queuePausedReason: queuePause !== null && !queueResuming ? queuePause.reason : null,
    resumeQueue,
    retryQueued,
  };
}
